import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatDT, getFileTimestamp } from '../utils/timezone';
import { setQuietHoursPrefs } from '../utils/quietHours';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { MobileSectionMenu } from '../components/shared/MobileSectionMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { CheckboxOption } from '../components/shared/CheckboxOption';
import { PaginationFooter, paginateArray, usePersistedPageSize } from '../components/shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useDebouncedValue, useSkipFirstEffect } from '../components/shared/useUrlPagination';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import {
  isPushSupported,
  getPermissionState,
  registerServiceWorker,
  getExistingSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from '../utils/pushNotifications';
import { deviceDisplayName } from '../utils/deviceName';
import { AuditLogList } from '../components/shared/AuditLogList';
import { ActivityTrailTab } from '../components/shared/ActivityTrailTab';
import { AllocationOverview } from '../components/settings/AllocationOverview';
import { DeviceSecurityCard } from '../components/settings/DeviceSecurityCard';
import { formatUserDateTime, formatUserDateTimeWithUTC } from '../utils/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { ALERT_TONES, ALERT_DURATIONS } from '../constants/alertTones';
import { playAlertTone } from '../components/events/EventNotificationBell';
import { CALL_RINGTONE_OPTIONS, previewRingtone, setStoredRingtoneChoice, loadCustomRingtoneUrl, clearCustomRingtoneCache } from '../utils/callRingtones';
import {
  Settings as SettingsIcon,
  Globe,
  Bell,
  Link as LinkIcon,
  Loader2,
  Save,
  RefreshCw,
  Key,
  Mail,
  Phone,
  Clock,
  Moon,
  MapPin,
  Webhook,
  Shield,
  Monitor,
  LogOut,
  Zap,
  Database,
  Eye,
  EyeOff,
  Copy,
  Trash2,
  Plus,
  TestTube,
  CalendarIcon,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  User,
  Pencil,
  Search,
  ExternalLink,
  Link2,
  ChevronRight,
  ChevronDown,
  Download,
  Upload,
  Smartphone,
  MessageSquare,
  X,
  Wifi,
  WifiOff,
  Activity,
  Circle,
  QrCode,
  History as HistoryIcon,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { TimezoneSelector } from '../components/TimezoneSelector';

// PrivilegeTreeNode component for rendering the tree (matching Admin Panel style)
// Moved outside Settings component to avoid re-creation on each render
// Driving Behaviour score rating thresholds (account-wide): below min → "Not yet
// rated"; between min and low-confidence → score shown with a Low confidence tag.
const BehaviourScoreSettingsCard = ({ api }) => {
  const [vals, setVals] = useState({ min_rated_km: '', low_confidence_km: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/behaviour-settings')
      .then((r) => setVals({ min_rated_km: String(r.data.min_rated_km), low_confidence_km: String(r.data.low_confidence_km) }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/behaviour-settings', {
        min_rated_km: Number(vals.min_rated_km),
        low_confidence_km: Number(vals.low_confidence_km),
      });
      setVals({ min_rated_km: String(r.data.min_rated_km), low_confidence_km: String(r.data.low_confidence_km) });
      toast.success('Driving Behaviour thresholds saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save thresholds');
    } finally { setSaving(false); }
  };
  return (
    <Card data-testid="behaviour-score-settings-card">
      <CardHeader>
        <CardTitle className="text-base">Driving Behaviour Score</CardTitle>
        <CardDescription>
          Scores need enough distance to be fair. Below the minimum, drivers and vehicles show "Not yet rated";
          between the two values the score carries a "Low confidence" tag. Applies to driver, staff and vehicle scores.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Minimum rated distance (km)</Label>
            <Input type="number" min="0" value={vals.min_rated_km} onChange={(e) => setVals((p) => ({ ...p, min_rated_km: e.target.value }))} className="dark:bg-[#172031]" data-testid="behaviour-min-rated-km-input" />
            <p className="text-xs text-muted-foreground">Under this distance: "Not yet rated" (default 50 km)</p>
          </div>
          <div className="space-y-2">
            <Label>Low confidence below (km)</Label>
            <Input type="number" min="0" value={vals.low_confidence_km} onChange={(e) => setVals((p) => ({ ...p, low_confidence_km: e.target.value }))} className="dark:bg-[#172031]" data-testid="behaviour-low-confidence-km-input" />
            <p className="text-xs text-muted-foreground">Under this distance: score shows a "Low confidence" tag (default 200 km)</p>
          </div>
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="behaviour-settings-save-button">
          {saving ? 'Saving…' : 'Save Thresholds'}
        </Button>
      </CardContent>
    </Card>
  );
};

const PrivilegeTreeNode = ({ node, selectedFeatures, onToggle, level = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedFeatures[node.id] === true;
  const isPartiallySelected = hasChildren && node.children.some(child => 
    selectedFeatures[child.id] === true
  );

  return (
    <div className={`${level > 0 ? 'ml-6' : ''}`} data-testid={`sub-account-privilege-tree-node-${node.id}`}>
      <div className="flex items-center gap-2 py-2 hover:bg-muted/50 px-2 rounded">
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5" data-testid={`sub-account-privilege-tree-expand-${node.id}`}>
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <div className="w-5" />
        )}
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(node.id)}
          className={isPartiallySelected && !isSelected ? 'opacity-50' : ''}
          data-testid={`sub-account-privilege-tree-checkbox-${node.id}`}
        />
        <label 
          className="text-sm cursor-pointer flex-1" 
          onClick={() => onToggle(node.id)}
          data-testid={`sub-account-privilege-tree-label-${node.id}`}
        >
          {node.label}
        </label>
      </div>
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <PrivilegeTreeNode
              key={child.id}
              node={child}
              selectedFeatures={selectedFeatures}
              onToggle={onToggle}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Shared empty shape for the Sub Account add/edit form so resets stay consistent (DRY).
const EMPTY_SUB_ACCOUNT_FORM = {
  full_name: '',
  email: '',
  username: '',
  password: '',
  mobile: '',
  is_active: true,
  expiry_enabled: false,
  expiry_date: '',
  selected_features: {},
  selected_device_ids: [],
  selected_person_ids: [],
  // Welcome invitation (mirrors the Admin "Add New User" modal)
  send_invite: true,
  invite_email: true,
  invite_sms: false,
};

export const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  // ── Logged-in device sessions & trust memory → DeviceSecurityCard component ──
  const { isSubpageEnabled, isFeatureEnabled, isNotificationChannelActive, setNotificationPrefs } = useFeatureFlags();
  const { persistedActiveTab, setPersistedActiveTab } = useSettings();
  
  // Check which subpages are enabled - Uses feature flags
  const hasGeneral = isSubpageEnabled('settings_general');
  const hasNotifications = isSubpageEnabled('settings_notifications');
  const hasIntegrations = isSubpageEnabled('settings_integrations');
  // Sub Accounts - Only for End Users AND requires the feature flag to be enabled
  const hasSubAccounts = isSubpageEnabled('settings_sub_accounts') && user?.role === 'end_user';
  const hasActivity = isSubpageEnabled('settings_activity_trail') && user?.role !== 'chat_contact';
  
  // SECURITY FIX: Return null if no subpages enabled
  const getDefaultTab = () => {
    if (hasGeneral) return 'general';
    if (hasNotifications) return 'notifications';
    if (hasIntegrations) return 'integrations';
    if (hasSubAccounts) return 'sub_accounts';
    if (hasActivity) return 'activity';
    return null;
  };

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const getRequestedTab = () => {
    const allowedTabs = { general: hasGeneral, notifications: hasNotifications, integrations: hasIntegrations, sub_accounts: hasSubAccounts, activity: hasActivity };
    return allowedTabs[requestedTab] ? requestedTab : null;
  };
  
  const [activeTab, setActiveTab] = useState(() => {
    const requested = getRequestedTab();
    if (requested) return requested;
    const allowedTabs = { general: hasGeneral, notifications: hasNotifications, integrations: hasIntegrations, sub_accounts: hasSubAccounts, activity: hasActivity };
    if (persistedActiveTab && allowedTabs[persistedActiveTab]) return persistedActiveTab;
    return getDefaultTab();
  });

  // Mirror activeTab back into persistence (URL still wins on initial load)
  useEffect(() => {
    if (activeTab && activeTab !== persistedActiveTab) setPersistedActiveTab(activeTab);
  }, [activeTab, persistedActiveTab, setPersistedActiveTab]);
  
  // SECURITY FIX: Update activeTab when feature flags load
  useEffect(() => {
    const requested = getRequestedTab();
    if (requested && activeTab !== requested) {
      setActiveTab(requested);
      return;
    }
    const allowedTabs = { general: hasGeneral, notifications: hasNotifications, integrations: hasIntegrations, sub_accounts: hasSubAccounts, activity: hasActivity };
    if (!activeTab || allowedTabs[activeTab] !== true) {
      const defaultTab = getDefaultTab();
      if (defaultTab) {
        setActiveTab(defaultTab);
      }
    }
  // getRequestedTab/getDefaultTab only read `requestedTab` and the has* flags, all
  // already in this dependency array, so the effect re-runs correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, activeTab, hasGeneral, hasNotifications, hasIntegrations, hasSubAccounts, hasActivity]);

  // Deep-link tab change handler: updates state + URL `?tab=` (replace mode so
  // tab clicks don't pollute browser history). Feature-gate enforced.
  const handleSettingsTabChange = useCallback((tab) => {
    const allowedTabs = { general: hasGeneral, notifications: hasNotifications, integrations: hasIntegrations, sub_accounts: hasSubAccounts, activity: hasActivity };
    if (!allowedTabs[tab]) return;
    setActiveTab(tab);
    navigate(`/settings?tab=${tab}`, { replace: true });
  }, [hasGeneral, hasNotifications, hasIntegrations, hasSubAccounts, hasActivity, navigate]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [retentionPolicy, setRetentionPolicy] = useState({
    global_operational_history_days: 365,
    user_history_retention_days: 365,
    effective_operational_history_days: 365,
    video_metadata_days: 90,
    chat_messages_days: 90,
  });
  
  // General Settings state
  const [generalSettings, setGeneralSettings] = useState({
    distance_unit: 'km',
    speed_unit: 'kmh',
    timezone: 'Australia/Sydney',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    language: 'en',
  });
  
  // Notifications Settings state
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: true,
    sms_enabled: true,
    push_enabled: true,
    chat_push_enabled: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '07:00',
    quiet_hours_weekend_enabled: false,
    quiet_hours_weekend_start: '23:00',
    quiet_hours_weekend_end: '09:00',
    critical_override: true,
    driver_confirmation_sound_enabled: true,
    driver_confirmation_sound_file: 'default',
    driver_confirmation_sound_duration: 'once',
    driver_confirmation_popup_enabled: true,
    driver_confirmation_push_enabled: true,
    security_alerts_push_enabled: true,
    call_ringtone: 'classic',
    call_ringtone_name: null,
  });
  const ringtoneFileInputRef = useRef(null);
  const [ringtoneUploading, setRingtoneUploading] = useState(false);
  const ringtonePreviewStopRef = useRef(null);

  const saveRingtoneChoice = async (choice) => {
    ringtonePreviewStopRef.current?.();
    setNotificationSettings(prev => ({ ...prev, call_ringtone: choice }));
    setStoredRingtoneChoice(choice);
    try {
      await api.put('/settings/notifications', { call_ringtone: choice });
    } catch (e) {
      toast.error('Could not save ringtone choice');
    }
  };

  const handleRingtonePreview = async () => {
    ringtonePreviewStopRef.current?.();
    const choice = notificationSettings.call_ringtone || 'classic';
    let customUrl = null;
    if (choice === 'custom') customUrl = await loadCustomRingtoneUrl(api);
    ringtonePreviewStopRef.current = previewRingtone(choice, customUrl);
  };

  const handleRingtoneUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error('Ringtone file must be 1MB or smaller');
      return;
    }
    setRingtoneUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/settings/call-ringtone/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      clearCustomRingtoneCache();
      await loadCustomRingtoneUrl(api);
      setNotificationSettings(prev => ({ ...prev, call_ringtone: 'custom', call_ringtone_name: data.call_ringtone_name }));
      setStoredRingtoneChoice('custom');
      toast.success('Custom ringtone uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload ringtone');
    } finally {
      setRingtoneUploading(false);
    }
  };

  const handleRingtoneRemove = async () => {
    ringtonePreviewStopRef.current?.();
    try {
      await api.delete('/settings/call-ringtone');
      clearCustomRingtoneCache();
      setNotificationSettings(prev => ({ ...prev, call_ringtone: 'classic', call_ringtone_name: null }));
      setStoredRingtoneChoice('classic');
      toast.success('Custom ringtone removed');
    } catch (e) {
      toast.error('Could not remove ringtone');
    }
  };
  const [pushSubscriptions, setPushSubscriptions] = useState([]);
  const [loadingPushSubscriptions, setLoadingPushSubscriptions] = useState(false);
  const [currentPushEndpoint, setCurrentPushEndpoint] = useState('');
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const lastKnownPushSubscriptionRef = useRef({ endpoint: '', seenAt: 0 });
  const consecutiveNullPushSyncsRef = useRef(0);
  const explicitDeviceUnsubscribeRef = useRef(false);

  const syncCurrentPushState = useCallback(async () => {
    if (!isPushSupported()) {
      setPushPermission('unsupported');
      setPushSubscribed(false);
      setCurrentPushEndpoint('');
      lastKnownPushSubscriptionRef.current = { endpoint: '', seenAt: 0 };
      consecutiveNullPushSyncsRef.current = 0;
      return null;
    }

    const permission = getPermissionState();
    setPushPermission(permission);
    const sub = await getExistingSubscription();
    if (sub?.endpoint) {
      consecutiveNullPushSyncsRef.current = 0;
      lastKnownPushSubscriptionRef.current = { endpoint: sub.endpoint, seenAt: Date.now() };
      setPushSubscribed(true);
      setCurrentPushEndpoint(sub.endpoint);
      return sub;
    }

    consecutiveNullPushSyncsRef.current += 1;
    const lastKnown = lastKnownPushSubscriptionRef.current;
    const keepLastKnownState =
      !explicitDeviceUnsubscribeRef.current &&
      permission === 'granted' &&
      !!lastKnown.endpoint &&
      Date.now() - lastKnown.seenAt < 10 * 60 * 1000 &&
      consecutiveNullPushSyncsRef.current < 3;

    if (keepLastKnownState) {
      setPushSubscribed(true);
      setCurrentPushEndpoint(lastKnown.endpoint);
      return null;
    }

    lastKnownPushSubscriptionRef.current = { endpoint: '', seenAt: 0 };
    setPushSubscribed(false);
    setCurrentPushEndpoint('');
    return sub;
  }, []);

  const persistPushPreference = useCallback(async (enabled) => {
    const response = await api.put('/settings/notifications', { push_enabled: enabled });
    setNotificationSettings(prev => ({ ...prev, push_enabled: enabled }));
    setNotificationPrefs(prev => ({
      ...prev,
      push_enabled: response?.data?.push_enabled ?? enabled,
    }));
    return response?.data;
  }, [api, setNotificationPrefs]);
  
  // Integrations Settings state
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [isAddApiKeyDialogOpen, setIsAddApiKeyDialogOpen] = useState(false);
  const [isAddWebhookDialogOpen, setIsAddWebhookDialogOpen] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState({
    name: '',
    permissions: 'read',
    ip_allowlist: '',
  });
  const [webhookForm, setWebhookForm] = useState({
    url: '',
    secret: '',
    events: [],
  });

  // Sub Accounts state (for End Users)
  const [subAccounts, setSubAccounts] = useState([]);
  const [subAccountsLoading, setSubAccountsLoading] = useState(false);
  // Sticks to true after the first sub-accounts fetch so refetches don't blank
  // the table and cause a search-bar flash / focus loss.
  const [subAccountsInitialLoadDone, setSubAccountsInitialLoadDone] = useState(false);
  const [pendingSettingsAction, setPendingSettingsAction] = useState(null);
  const [settingsDeleteConfirmText, setSettingsDeleteConfirmText] = useState('');
  // Snapshot of the Edit form taken when the dialog opens — the unsaved-changes
  // warning compares actual form data against it instead of trusting DOM input
  // events (browser autofill fired spurious `input` events, flagging untouched
  // forms as dirty).
  const editSubAccountSnapshotRef = useRef('');
  // Normalised so change+revert never trips the warning: feature keys that are
  // falsy are dropped (toggling on/off adds `key: false` that wasn't there),
  // feature keys are sorted, and device ids compared order-insensitively.
  const normalizeSubAccountForm = (form) => JSON.stringify({
    ...form,
    selected_features: Object.fromEntries(
      Object.entries(form.selected_features || {}).filter(([, v]) => !!v).sort((a, b) => a[0].localeCompare(b[0]))
    ),
    selected_device_ids: [...(form.selected_device_ids || [])].sort(),
    selected_person_ids: [...(form.selected_person_ids || [])].sort(),
  });
  const subAccountFormHasChanges = () =>
    normalizeSubAccountForm(subAccountForm) !== editSubAccountSnapshotRef.current;
  const [showSubAccountUnsavedWarning, setShowSubAccountUnsavedWarning] = useState(false);
  const [isAddSubAccountDialogOpen, setIsAddSubAccountDialogOpen] = useState(false);
  const [isEditSubAccountDialogOpen, setIsEditSubAccountDialogOpen] = useState(false);
  const [selectedSubAccount, setSelectedSubAccount] = useState(null);
  const [subAccountForm, setSubAccountForm] = useState({ ...EMPTY_SUB_ACCOUNT_FORM });
  const [subAccountSearchQuery, setSubAccountSearchQuery] = useState('');
  const [subAccountsPage, setSubAccountsPage] = useUrlPage('page');
  const [subAccountsPageSize, setSubAccountsPageSize] = useUrlPageSize('sub-accounts', 10);
  const [subAccountsTotal, setSubAccountsTotal] = useState(0);
  // Unfiltered total for the "Sub Account Limit X / Y" chip so it always
  // reflects the tenant's real usage, not the currently-filtered slice.
  const [subAccountsUnfilteredTotal, setSubAccountsUnfilteredTotal] = useState(0);
  const [subAccountsPendingTotal, setSubAccountsPendingTotal] = useState(0);
  const [subAccountsSelectAllLoading, setSubAccountsSelectAllLoading] = useState(false);
  const [maxSubAccounts, setMaxSubAccounts] = useState(0);
  const [selectedSubAccountIds, setSelectedSubAccountIds] = useState([]);  // For batch selection
  const [myFeatures, setMyFeatures] = useState({});
  const [myDevices, setMyDevices] = useState([]);
  const [quickLoginLoading, setQuickLoginLoading] = useState(false);
  const [quickLoginUrl, setQuickLoginUrl] = useState('');
  const [showSubQuickLoginQR, setShowSubQuickLoginQR] = useState(false);
  const [activeSubAccountTab, setActiveSubAccountTab] = useState('account');
  const [deviceSearchFilter, setDeviceSearchFilter] = useState('');
  const [subAccountPendingOnly, setSubAccountPendingOnly] = useState(false);  // Filter to pending/expired invites
  // Sub Account audit trail (Audit tab in the Edit Sub Account dialog)
  const [subAccountAuditLogs, setSubAccountAuditLogs] = useState([]);
  const [subAccountAuditLoading, setSubAccountAuditLoading] = useState(false);
  // Resend-invitation delivery channel selection (Edit Sub Account dialog → Account Activation)
  const [resendSubInviteEmail, setResendSubInviteEmail] = useState(true);
  const [resendSubInviteSms, setResendSubInviteSms] = useState(false);
  const [subInviteLoading, setSubInviteLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [generalRes, notificationsRes, apiKeysRes, webhooksRes, retentionPolicyRes] = await Promise.all([
        api.get('/settings/general').catch(() => ({ data: generalSettings })),
        api.get('/settings/notifications').catch(() => ({ data: null })),
        api.get('/settings/api-keys').catch(() => ({ data: [] })),
        api.get('/settings/webhooks').catch(() => ({ data: [] })),
        api.get('/settings/data-retention-policy').catch(() => ({
          data: {
            global_operational_history_days: 365,
            user_history_retention_days: 365,
            effective_operational_history_days: 365,
          }
        })),
      ]);
      
      setGeneralSettings(generalRes.data);
      if (notificationsRes.data) {
        setNotificationSettings(prev => ({ ...prev, ...notificationsRes.data }));
      }
      setApiKeys(Array.isArray(apiKeysRes.data) ? apiKeysRes.data : []);
      setWebhooks(Array.isArray(webhooksRes.data) ? webhooksRes.data : []);
      setRetentionPolicy(prev => ({ ...prev, ...(retentionPolicyRes.data || {}) }));

      // Check push subscription status without blocking the Settings shell.
      if (isPushSupported()) {
        Promise.race([
          syncCurrentPushState(),
          new Promise((resolve) => setTimeout(resolve, 2500)),
        ]).catch((err) => console.warn('Push state sync skipped:', err));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setApiKeys([]);
      setWebhooks([]);
    } finally {
      setLoading(false);
    }
  // generalSettings is read only as the fallback default for the /settings/general
  // request; it's also written by this loader, so adding it as a dep would loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, syncCurrentPushState, user]);

  const fetchPushSubscriptions = useCallback(async () => {
    if (!isFeatureEnabled('notification_push')) {
      setPushSubscriptions([]);
      return;
    }
    setLoadingPushSubscriptions(true);
    try {
      const res = await api.get('/push/subscriptions');
      const subs = Array.isArray(res.data) ? res.data : [];
      subs.sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
      setPushSubscriptions(subs);
    } catch (error) {
      console.error('Failed to fetch push subscriptions:', error);
      setPushSubscriptions([]);
    } finally {
      setLoadingPushSubscriptions(false);
    }
  }, [api, isFeatureEnabled]);

  useEffect(() => {
    fetchData();
    // Register service worker on mount
    if (isPushSupported()) {
      registerServiceWorker();
    }
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchPushSubscriptions();
    }
  }, [activeTab, fetchPushSubscriptions]);

  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      await api.put('/settings/general', generalSettings);
      // Refresh the timezone cache so the rest of the app picks up the new value
      try {
        const { loadTimezoneSettings } = await import('../utils/timezone');
        await loadTimezoneSettings(api);
      } catch (_) { /* non-critical */ }
      toast.success('General settings saved');
    } catch (error) {
      toast.error('Failed to save general settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await api.put('/settings/notifications', {
        email_enabled: notificationSettings.email_enabled,
        sms_enabled: notificationSettings.sms_enabled,
        push_enabled: notificationSettings.push_enabled,
        chat_push_enabled: notificationSettings.chat_push_enabled,
        quiet_hours_enabled: notificationSettings.quiet_hours_enabled,
        quiet_hours_start: notificationSettings.quiet_hours_start,
        quiet_hours_end: notificationSettings.quiet_hours_end,
        quiet_hours_weekend_enabled: notificationSettings.quiet_hours_weekend_enabled,
        quiet_hours_weekend_start: notificationSettings.quiet_hours_weekend_start,
        quiet_hours_weekend_end: notificationSettings.quiet_hours_weekend_end,
        critical_override: notificationSettings.critical_override,
        driver_confirmation_sound_enabled: notificationSettings.driver_confirmation_sound_enabled,
        driver_confirmation_sound_file: notificationSettings.driver_confirmation_sound_file,
        driver_confirmation_sound_duration: notificationSettings.driver_confirmation_sound_duration,
        driver_confirmation_popup_enabled: notificationSettings.driver_confirmation_popup_enabled,
        driver_confirmation_push_enabled: notificationSettings.driver_confirmation_push_enabled,
      });
      // Sync to FeatureFlagsContext so other components reflect the change immediately
      setNotificationPrefs({
        email_enabled: notificationSettings.email_enabled,
        sms_enabled: notificationSettings.sms_enabled,
        push_enabled: notificationSettings.push_enabled,
      });
      toast.success('Notification settings saved');
      setQuietHoursPrefs(notificationSettings);
    } catch (error) {
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAccountPushToggle = async (enabled) => {
    setPushLoading(true);
    try {
      if (enabled) {
        explicitDeviceUnsubscribeRef.current = false;
        await persistPushPreference(true);
        await syncCurrentPushState();
        await fetchPushSubscriptions();
        toast.success('Push notifications enabled for this account');
      } else {
        explicitDeviceUnsubscribeRef.current = true;
        await persistPushPreference(false);
        await unsubscribeFromPush(api);
        await api.delete('/push/unsubscribe');
        lastKnownPushSubscriptionRef.current = { endpoint: '', seenAt: 0 };
        consecutiveNullPushSyncsRef.current = 0;
        await syncCurrentPushState();
        setPushSubscriptions([]);
        toast.success('Push notifications disabled for this account and all devices were unsubscribed');
      }
    } catch (error) {
      console.error('Account push toggle error:', error);
      toast.error(error.message || 'Failed to update account push notifications');
    } finally {
      explicitDeviceUnsubscribeRef.current = false;
      setPushLoading(false);
    }
  };

  const handleCurrentDevicePushToggle = async (enabled) => {
    setPushLoading(true);
    try {
      if (!notificationSettings.push_enabled) {
        toast.error('Turn on account push notifications first');
        return;
      }

      if (enabled) {
        explicitDeviceUnsubscribeRef.current = false;
        if (!isPushSupported()) {
          toast.error('Push notifications are not supported in this browser');
          return;
        }
        const permission = getPermissionState();
        if (permission === 'denied') {
          toast.error('Push notifications are blocked. Please enable them in your browser settings.');
          return;
        }
        await subscribeToPush(api);
        await syncCurrentPushState();
        await fetchPushSubscriptions();
        toast.success('This device is now subscribed to push notifications');
      } else {
        explicitDeviceUnsubscribeRef.current = true;
        await unsubscribeFromPush(api);
        lastKnownPushSubscriptionRef.current = { endpoint: '', seenAt: 0 };
        consecutiveNullPushSyncsRef.current = 0;
        await syncCurrentPushState();
        await fetchPushSubscriptions();
        toast.success('This device was unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Current device push toggle error:', error);
      if (error.message?.includes('denied')) {
        setPushPermission('denied');
        toast.error('Push notification permission was denied by the browser');
      } else {
        toast.error(error.message || 'Failed to update this device push subscription');
      }
    } finally {
      explicitDeviceUnsubscribeRef.current = false;
      setPushLoading(false);
    }
  };

  const handleRemovePushSubscription = async (endpoint) => {
    try {
      if (currentPushEndpoint && endpoint === currentPushEndpoint) {
        await unsubscribeFromPush(api);
        await syncCurrentPushState();
      } else {
        await api.post('/push/unsubscribe-endpoint', { endpoint });
      }
      await fetchPushSubscriptions();
      toast.success('Push subscription removed');
    } catch (error) {
      console.error('Failed to remove push subscription:', error);
      toast.error('Failed to remove push subscription');
    }
  };

  const formatSubscriptionTime = (value) => {
    if (!value) return 'Unknown';
    return formatDT(value);
  };

  const accountPushEnabled = notificationSettings.push_enabled;
  // Driver-confirmation ballots only reach the account owner (with People access) and
  // Sub Accounts with allocated devices — hide the related settings from everyone else.
  const canReceiveDriverBallots = user?.role === 'sub_account' || (user?.role === 'end_user' && isFeatureEnabled('people_page'));

  // Instant-save a single notification preference field (used by the preference grid).
  const savePrefField = async (field, value, label) => {
    setNotificationSettings(prev => ({ ...prev, [field]: value }));
    try {
      await api.put('/settings/notifications', { [field]: value });
      toast.success(`${label} ${value ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(`Failed to save ${label}`);
      setNotificationSettings(prev => ({ ...prev, [field]: !value }));
    }
  };
  const currentDeviceRegistered = !!(currentPushEndpoint && pushSubscriptions.some((sub) => sub.subscription_data?.endpoint === currentPushEndpoint && sub.stale !== true));
  const thisDeviceSubscribed = accountPushEnabled && pushSubscribed && currentDeviceRegistered;
  const localSubscriptionNeedsResync = accountPushEnabled && pushSubscribed && !currentDeviceRegistered;

  const formatEndpointPreview = (endpoint) => {
    if (!endpoint) return 'Unknown endpoint';
    if (endpoint.length <= 68) return endpoint;
    return `${endpoint.slice(0, 44)}…${endpoint.slice(-20)}`;
  };

  const pushStatusMeta = (status) => {
    switch (status) {
      case 'success':
        return { label: 'Success', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' };
      case 'failed':
        return { label: 'Failed', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
      case 'stale':
        return { label: 'Stale', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
      case 'sending':
        return { label: 'Sending', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
      default:
        return { label: 'Not tested', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' };
    }
  };

  const handleCreateApiKey = async () => {
    if (!apiKeyForm.name) {
      toast.error('Please provide a name for the API key');
      return;
    }
    try {
      await api.post('/settings/api-keys', apiKeyForm);
      toast.success('API key created successfully');
      setIsAddApiKeyDialogOpen(false);
      setApiKeyForm({ name: '', permissions: 'read', ip_allowlist: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleCreateWebhook = async () => {
    if (!webhookForm.url) {
      toast.error('Please provide webhook URL');
      return;
    }
    try {
      await api.post('/settings/webhooks', webhookForm);
      toast.success('Webhook created successfully');
      setIsAddWebhookDialogOpen(false);
      setWebhookForm({ url: '', secret: '', events: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    }
  };

  // =============== SUB ACCOUNTS FUNCTIONS ===============
  const debouncedSubAccountSearch = useDebouncedValue(subAccountSearchQuery, 400);

  // Server-side filter params shared by list fetch, Select All and CSV export.
  const buildSubAccountParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('include_sub_accounts', 'true');
    params.set('role_filter', 'sub_account');
    if (user?.id) params.set('parent_user_id', user.id);
    if (debouncedSubAccountSearch.trim()) params.set('search', debouncedSubAccountSearch.trim());
    if (subAccountPendingOnly) params.set('invite_pending_only', 'true');
    return params;
  }, [user?.id, debouncedSubAccountSearch, subAccountPendingOnly]);

  const fetchSubAccounts = useCallback(async () => {
    if (!hasSubAccounts) return;
    
    setSubAccountsLoading(true);
    try {
      // Server-side pagination — GET /users applies role/parent/search/pending
      // filters and returns the total row count in X-Total-Count.
      const params = buildSubAccountParams();
      params.set('skip', String((subAccountsPage - 1) * subAccountsPageSize));
      params.set('limit', String(subAccountsPageSize));
      const response = await api.get(`/users?${params.toString()}`);
      setSubAccounts(response.data || []);
      const total = parseInt(response.headers?.['x-total-count'], 10);
      setSubAccountsTotal(Number.isFinite(total) ? total : (response.data || []).length);

      // Pending-invite total for the quick-filter banner (header-only count).
      const pendingParams = new URLSearchParams();
      pendingParams.set('include_sub_accounts', 'true');
      pendingParams.set('role_filter', 'sub_account');
      if (user?.id) pendingParams.set('parent_user_id', user.id);
      pendingParams.set('invite_pending_only', 'true');
      pendingParams.set('limit', '1');
      const pendingRes = await api.get(`/users?${pendingParams.toString()}`);
      const pendingTotal = parseInt(pendingRes.headers?.['x-total-count'], 10);
      setSubAccountsPendingTotal(Number.isFinite(pendingTotal) ? pendingTotal : 0);

      // Unfiltered total — used ONLY by the "Sub Account Limit X / Y" chip so it
      // shows the tenant's real usage regardless of the current search/filter.
      // Skip the extra call when there is no active filter, since the paginated
      // fetch above already returns the same X-Total-Count.
      const hasActiveFilter = Boolean(debouncedSubAccountSearch.trim()) || subAccountPendingOnly;
      if (hasActiveFilter) {
        const unfilteredParams = new URLSearchParams();
        unfilteredParams.set('include_sub_accounts', 'true');
        unfilteredParams.set('role_filter', 'sub_account');
        if (user?.id) unfilteredParams.set('parent_user_id', user.id);
        unfilteredParams.set('limit', '1');
        const unfilteredRes = await api.get(`/users?${unfilteredParams.toString()}`);
        const unfilteredTotal = parseInt(unfilteredRes.headers?.['x-total-count'], 10);
        setSubAccountsUnfilteredTotal(Number.isFinite(unfilteredTotal) ? unfilteredTotal : 0);
      } else {
        setSubAccountsUnfilteredTotal(Number.isFinite(total) ? total : (response.data || []).length);
      }
      
      // Also fetch max_sub_accounts limit and my features
      const featuresRes = await api.get(`/users/${user?.id}/features`);
      setMaxSubAccounts(featuresRes.data.max_sub_accounts || 0);
      setMyFeatures(featuresRes.data.features || {});
      setMyDevices(featuresRes.data.assigned_device_ids || []);
    } catch (error) {
      console.error('Failed to fetch sub accounts:', error);
      setSubAccounts([]);
      setSubAccountsTotal(0);
      setSubAccountsUnfilteredTotal(0);
    } finally {
      setSubAccountsLoading(false);
      setSubAccountsInitialLoadDone(true);
    }
  }, [api, hasSubAccounts, user?.id, buildSubAccountParams, subAccountsPage, subAccountsPageSize, debouncedSubAccountSearch, subAccountPendingOnly]);

  // Reset to page 1 when a filter changes (skips mount so ?page= deep-links survive).
  useSkipFirstEffect(() => { setSubAccountsPage(1); }, [debouncedSubAccountSearch, subAccountPendingOnly, subAccountsPageSize]);

  // Export Sub Accounts to CSV
  const handleExportSubAccountsCSV = async () => {
    // Fetch ALL rows matching the current filters (no skip/limit = full set),
    // then narrow to the selection when non-empty.
    let accountsToExport = [];
    try {
      const params = buildSubAccountParams();
      params.set('limit', '10000');
      const res = await api.get(`/users?${params.toString()}`);
      const all = res.data || [];
      accountsToExport = selectedSubAccountIds.length > 0
        ? all.filter(sa => selectedSubAccountIds.includes(sa.id))
        : all;
    } catch (error) {
      toast.error('Failed to fetch sub accounts for export');
      return;
    }
      
    if (accountsToExport.length === 0) {
      toast.error('No sub accounts to export');
      return;
    }

    const headers = ['full_name', 'username', 'email', 'is_active', 'expiry_enabled', 'expiry_date', 'last_login', 'created_at'];
    const headerLabels = ['Full Name', 'Username', 'Email', 'Status', 'Expiry Enabled', 'Expiry Date', 'Last Login', 'Created At'];
    
    const csvContent = [
      headerLabels.join(','),
      ...accountsToExport.map(sa => headers.map(h => {
        let value = sa[h];
        if (h === 'is_active') value = value === false ? 'Blocked' : 'Active';
        if (h === 'expiry_enabled') value = value ? 'Yes' : 'No';
        if (h === 'expiry_date' && value) value = formatDT(value);
        if (h === 'last_login' && value) value = formatDT(value);
        if (h === 'created_at' && value) value = formatDT(value);
        if (typeof value === 'string' && value.includes(',')) value = `"${value}"`;
        return value || '';
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const _tssa = getFileTimestamp();
    link.download = `sub_accounts_export_${_tssa}.csv`;
    link.click();
    toast.success(`Exported ${accountsToExport.length} sub account(s)`);
    
    // Clear selection after export
    if (selectedSubAccountIds.length > 0) {
      setSelectedSubAccountIds([]);
    }
  };

  // Toggle single sub account selection
  const handleToggleSubAccountSelection = (subAccountId) => {
    setSelectedSubAccountIds(prev => 
      prev.includes(subAccountId) 
        ? prev.filter(id => id !== subAccountId)
        : [...prev, subAccountId]
    );
  };

  // Toggle all VISIBLE (current page) sub accounts — mirrors Shared Evidence
  const handleToggleAllSubAccounts = (checked) => {
    const pageIds = pagedSubAccounts.map(sa => sa.id);
    setSelectedSubAccountIds(prev => checked
      ? Array.from(new Set([...prev, ...pageIds]))
      : prev.filter(id => !pageIds.includes(id))
    );
  };

  // Delete selected sub accounts (batch delete)
  const handleDeleteSelectedSubAccounts = async () => {
    if (selectedSubAccountIds.length === 0) {
      toast.error('No sub accounts selected');
      return;
    }
    
    setPendingSettingsAction({ title: 'Delete Sub Accounts?', requireTyped: true, message: `Are you sure you want to delete ${selectedSubAccountIds.length} sub account(s)? This action cannot be undone.`, action: async () => {
      let successCount = 0;
      let errorCount = 0;
      for (const id of selectedSubAccountIds) {
        try { await api.delete(`/users/${id}`); successCount++; }
        catch (error) { errorCount++; }
      }
      if (successCount > 0) { toast.success(`Deleted ${successCount} sub account(s)`); fetchSubAccounts(); setSelectedSubAccountIds([]); }
      if (errorCount > 0) { toast.error(`Failed to delete ${errorCount} sub account(s)`); }
    }});
  };

  // Import Sub Accounts from CSV
  const handleImportSubAccountsCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          toast.error('CSV file is empty or has no data rows');
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/ /g, '_'));
        let imported = 0;
        let skipped = 0;
        let failed = 0;
        const errors = [];

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const rowData = {};
          headers.forEach((h, idx) => {
            rowData[h] = values[idx];
          });

          // Map CSV columns to expected fields
          const subAccountData = {
            full_name: rowData.full_name || rowData.name || '',
            username: rowData.username || '',
            email: rowData.email || '',
            password: rowData.password || 'TempPass123!', // Default password for imports
            is_active: rowData.status !== 'Blocked' && rowData.is_active !== 'Blocked',
          };

          if (!subAccountData.email || !subAccountData.full_name) {
            failed++;
            errors.push(`Row ${i}: Missing email or full name`);
            continue;
          }

          // Check if sub account with this email already exists
          const existingSubAccount = subAccounts.find(sa => sa.email.toLowerCase() === subAccountData.email.toLowerCase());
          if (existingSubAccount) {
            skipped++;
            continue; // Skip existing accounts silently
          }

          try {
            // Use the correct endpoint - POST /users with role: sub_account
            const username = subAccountData.username || subAccountData.email.split('@')[0];
            const createRes = await api.post('/users', {
              full_name: subAccountData.full_name,
              email: subAccountData.email,
              username: username,
              password: subAccountData.password,
              role: 'sub_account',
              is_active: subAccountData.is_active,
            });
            imported++;
          } catch (err) {
            const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
            console.error(`Failed to import row ${i}:`, errorMsg);
            failed++;
            errors.push(`Row ${i} (${subAccountData.email}): ${errorMsg}`);
          }
        }

        // Show results
        if (imported > 0) {
          toast.success(`Imported ${imported} sub account(s)${skipped > 0 ? `, ${skipped} skipped (already exist)` : ''}${failed > 0 ? `, ${failed} failed` : ''}`);
          fetchSubAccounts();
        } else if (skipped > 0 && failed === 0) {
          toast.info(`All ${skipped} sub account(s) already exist - nothing to import`);
        } else {
          toast.error(`Import failed: ${failed} row(s) could not be imported${errors.length > 0 ? '. ' + errors[0] : ''}`);
        }
      } catch (error) {
        console.error('CSV import error:', error);
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  // Fetch devices that I have access to
  const [availableDevices, setAvailableDevices] = useState([]);
  useEffect(() => {
    const fetchMyDevices = async () => {
      if (!hasSubAccounts) return;
      try {
        const devicesRes = await api.get('/gps/live');
        setAvailableDevices(devicesRes.data || []);
      } catch (error) {
        console.error('Failed to fetch devices:', error);
      }
    };
    if (activeTab === 'sub_accounts') {
      fetchMyDevices();
    }
  }, [api, hasSubAccounts, activeTab]);

  useEffect(() => {
    if (activeTab === 'sub_accounts' && hasSubAccounts) {
      fetchSubAccounts();
    }
  }, [activeTab, hasSubAccounts, fetchSubAccounts]);

  const handleCreateSubAccount = async () => {
    if (!subAccountForm.full_name || !subAccountForm.email) {
      toast.error('Please fill in required fields (Full Name and Email)');
      return;
    }
    if (!subAccountForm.send_invite && !subAccountForm.password) {
      toast.error('Enter a password, or enable "Send welcome invitation" to let the user set their own.');
      return;
    }
    
    // Check limit — uses the unfiltered total so filtering the view can never
    // trick the guard into allowing an over-cap creation.
    if (maxSubAccounts !== -1 && maxSubAccounts > 0 && subAccountsUnfilteredTotal >= maxSubAccounts) {
      toast.error(`Sub Account limit reached (${maxSubAccounts})`);
      return;
    }
    
    try {
      // Use provided username or generate from email
      const username = subAccountForm.username || subAccountForm.email.split('@')[0];

      // Build the invite delivery channels from the ticked + actually-deliverable options.
      // Respects the operator's Email/SMS notification privileges (backend re-enforces them).
      const inviteChannels = subAccountForm.send_invite
        ? [
            ...((subAccountForm.invite_email && (subAccountForm.email || '').trim() && isNotificationChannelActive('notification_email')) ? ['email'] : []),
            ...((subAccountForm.invite_sms && (subAccountForm.mobile || '').trim() && isNotificationChannelActive('notification_sms')) ? ['sms'] : []),
          ]
        : undefined;

      // Create the sub account
      const createRes = await api.post('/users', {
        full_name: subAccountForm.full_name,
        email: subAccountForm.email,
        username: username,
        password: subAccountForm.send_invite ? undefined : subAccountForm.password,
        mobile: subAccountForm.mobile || undefined,
        role: 'sub_account',
        is_active: subAccountForm.is_active,
        expiry_enabled: subAccountForm.expiry_enabled,
        expiry_date: subAccountForm.expiry_date || null,
        send_invite: subAccountForm.send_invite,
        invite_channels: inviteChannels,
      });
      
      const newUserId = createRes.data.id;
      
      // Update sub account's features and devices
      await api.put(`/users/${newUserId}/features`, {
        features: subAccountForm.selected_features,
        assigned_device_ids: subAccountForm.selected_device_ids,
        assigned_person_ids: subAccountForm.selected_person_ids,
      });

      const delivery = createRes.data?.invite_delivery || {};
      if (subAccountForm.send_invite) {
        const sentChans = Object.entries(delivery).filter(([, s]) => s === 'sent').map(([c]) => (c === 'sms' ? 'SMS' : 'Email'));
        toast.success(sentChans.length
          ? `Sub Account created — invitation sent via ${sentChans.join(' & ')}`
          : 'Sub Account created — invitation link is ready to copy from the account card');
        if (delivery.sms && delivery.sms !== 'sent') {
          const smsMsg = delivery.sms === 'not_configured'
            ? 'SMS not sent: no SMS provider is configured (contact your administrator).'
            : delivery.sms === 'no_recipient'
              ? 'SMS not sent: no mobile number on file.'
              : delivery.sms === 'not_allowed'
                ? 'SMS not sent: your SMS notification privilege is off.'
                : 'SMS delivery failed — you can resend or copy the link from the account card.';
          toast.warning(smsMsg);
        }
      } else {
        toast.success('Sub Account created successfully');
      }
      setIsAddSubAccountDialogOpen(false);
      setSubAccountForm({ ...EMPTY_SUB_ACCOUNT_FORM });
      fetchSubAccounts();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) and regular error messages
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to create Sub Account';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Extract message from first validation error
        errorMessage = detail[0]?.msg || detail[0]?.message || errorMessage;
      }
      
      toast.error(errorMessage);
    }
  };

  // Function to open Add Sub Account dialog with fresh/empty form
  const openAddSubAccountDialog = () => {
    setSubAccountForm({ ...EMPTY_SUB_ACCOUNT_FORM });
    setActiveSubAccountTab('account');
    setDeviceSearchFilter('');
    setSubDevicePage(1);
    setIsAddSubAccountDialogOpen(true);
  };

  // Helper function to parse expiry date - handles UTC dates from the API
  const parseExpiryDate = (dateString) => {
    if (!dateString) return null;
    // If the date doesn't have a timezone indicator, treat it as UTC
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      return new Date(dateString + 'Z');
    }
    return new Date(dateString);
  };

  // Helper function to check if a sub account is expired (frontend-side prediction)
  const isSubAccountExpired = (subAccount) => {
    if (!subAccount?.expiry_enabled || !subAccount?.expiry_date) return false;
    const expiryDate = parseExpiryDate(subAccount.expiry_date);
    return expiryDate && expiryDate < new Date();
  };

  // Handler for form input changes with auto-activate logic
  const handleSubAccountFormChange = (field, value) => {
    // Show confirmation when turning off expiry
    if (field === 'expiry_enabled' && value === false && subAccountForm.expiry_enabled === true) {
      setPendingSettingsAction({ title: 'Disable Account Expiry', message: 'Are you sure you want to disable Account Expiry? This will automatically activate the account and remove the expiry date restriction.', action: async () => {
        setSubAccountForm(prev => ({ ...prev, expiry_enabled: false, is_active: true }));
      }});
      return;
    }
    
    setSubAccountForm(prev => {
      const newData = { ...prev, [field]: value };
      
      // AUTO-ACTIVATE: When expiry is disabled, automatically activate the account
      if (field === 'expiry_enabled' && value === false && prev.expiry_enabled === true) {
        newData.is_active = true;
      }
      
      return newData;
    });
  };

  // Toggle a single device in the sub-account selection (clicking the whole row).
  const toggleSubAccountDevice = (deviceId) => {
    setSubAccountForm(prev => ({
      ...prev,
      selected_device_ids: prev.selected_device_ids.includes(deviceId)
        ? prev.selected_device_ids.filter(id => id !== deviceId)
        : [...prev.selected_device_ids, deviceId],
    }));
  };

  // Filter devices based on search. Phase 2D — include asset_name + unit_number
  // so the search box matches the same fields the row label is built from.
  // Sorted alphabetically by display name to match the Device Management page.
  const filteredAvailableDevices = availableDevices
    .filter(device => {
      if (!deviceSearchFilter) return true;
      const search = deviceSearchFilter.toLowerCase();
      return (
        device.name?.toLowerCase().includes(search) ||
        device.asset_name?.toLowerCase().includes(search) ||
        device.unit_number?.toLowerCase().includes(search) ||
        device.registration?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => deviceDisplayName(a).localeCompare(deviceDisplayName(b), undefined, { sensitivity: 'base' }));

  // Client-side pagination for the Sub Account device picker (Add + Edit dialogs).
  const [subDevicePage, setSubDevicePage] = useState(1);
  const [subDevicePageSize, setSubDevicePageSize] = usePersistedPageSize('subaccount-devices-v2', 25);
  const paginatedSubAccountDevices = paginateArray(filteredAvailableDevices, subDevicePage, subDevicePageSize);
  useEffect(() => { setSubDevicePage(1); }, [deviceSearchFilter]);

  // Status badge matching Admin Panel → Device Management colours/icons exactly.
  const getSubAccountDeviceStatusBadge = (status) => {
    const statusConfig = {
      online: { color: 'bg-green-600', label: 'Online', icon: Wifi },
      offline: { color: 'bg-gray-500', label: 'Offline', icon: WifiOff },
      moving: { color: 'bg-blue-600', label: 'Moving', icon: Activity },
      stopped: { color: 'bg-red-600', label: 'Stopped', icon: Circle },
      idle: { color: 'bg-yellow-600', label: 'Idle', icon: Circle },
    };
    const config = statusConfig[status] || statusConfig.offline;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // App-standard selection bar (matches Device Management / People / Fleet):
  // "N selected" + Select All / Deselect All (across all pages of the current
  // search) + Clear. Shared by the Add + Edit dialogs.
  const renderSubAccountDeviceSelectionBar = () => {
    const selectedCount = subAccountForm.selected_device_ids.length;
    const allFilteredSelected = filteredAvailableDevices.length > 0 &&
      filteredAvailableDevices.every(d => subAccountForm.selected_device_ids.includes(d.id));
    return (
      <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="subaccount-devices-selection-bar">
        <span data-testid="subaccount-devices-selection-count">{selectedCount} selected</span>
        {selectedCount > 0 && (<>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => {
              const filterIds = filteredAvailableDevices.map(d => d.id);
              setSubAccountForm(prev => ({
                ...prev,
                selected_device_ids: allFilteredSelected
                  ? prev.selected_device_ids.filter(id => !filterIds.includes(id))
                  : [...new Set([...prev.selected_device_ids, ...filterIds])],
              }));
            }}
            data-testid="subaccount-devices-select-all-toggle"
          >
            {allFilteredSelected ? 'Deselect All' : 'Select All'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setSubAccountForm(prev => ({ ...prev, selected_device_ids: [] }))}
            data-testid="subaccount-devices-selection-clear"
          >
            Clear
          </Button>
        </>)}
      </div>
    );
  };

  const handleEditSubAccount = async (subAccount) => {
    // Fetch fresh user data to ensure we have the latest status
    // This handles cases where background tasks may have blocked an expired account
    let freshSubAccount = subAccount;
    try {
      const userRes = await api.get(`/users/${subAccount.id}`);
      if (userRes.data) {
        freshSubAccount = userRes.data;
      }
    } catch (error) {
      console.error('Failed to fetch fresh sub account data:', error);
      // Continue with stale data if fetch fails
    }
    
    setSelectedSubAccount(freshSubAccount);
    // Reset quick login URL
    if (freshSubAccount.quick_login_token) {
      setQuickLoginUrl(`${window.location.origin}/quick-login/${freshSubAccount.quick_login_token}`);
    } else {
      setQuickLoginUrl('');
    }
    // Reset tab and device filter
    setActiveSubAccountTab('account');
    setShowSubQuickLoginQR(false);
    setDeviceSearchFilter('');
    setSubDevicePage(1);
    // Reset resend-invite channel ticks to defaults (email on, sms off)
    setResendSubInviteEmail(true);
    setResendSubInviteSms(false);
    // Load sub account's current features and devices
    try {
      const res = await api.get(`/users/${freshSubAccount.id}/features`);
      const loadedForm = {
        ...EMPTY_SUB_ACCOUNT_FORM,
        full_name: freshSubAccount.full_name,
        email: freshSubAccount.email,
        username: freshSubAccount.username || '',
        mobile: freshSubAccount.mobile || '',
        is_active: freshSubAccount.is_active !== false,
        expiry_enabled: freshSubAccount.expiry_enabled || false,
        expiry_date: freshSubAccount.expiry_date || '',
        selected_features: res.data.features || {},
        selected_device_ids: res.data.assigned_device_ids || [],
        selected_person_ids: res.data.assigned_person_ids || [],
      };
      setSubAccountForm(loadedForm);
      editSubAccountSnapshotRef.current = normalizeSubAccountForm(loadedForm);
      setIsEditSubAccountDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch sub account features:', err);
      toast.error('Failed to load sub account details');
    }
  };

  // ── Account Activation (welcome invitation) for a Sub Account ──────────────
  // Mirrors the Admin [Edit User] dialog: Resend (Email/SMS), Copy link, Cancel.
  // Backend re-enforces the operator's Email/SMS notification privileges.
  const handleResendSubAccountInvite = async () => {
    if (!selectedSubAccount) return;
    const emailActive = !!((selectedSubAccount.email || '').trim()) && isNotificationChannelActive('notification_email');
    const smsActive = !!((selectedSubAccount.mobile || '').trim()) && isNotificationChannelActive('notification_sms');
    const channels = [
      ...(resendSubInviteEmail && emailActive ? ['email'] : []),
      ...(resendSubInviteSms && smsActive ? ['sms'] : []),
    ];
    setSubInviteLoading(true);
    try {
      const res = await api.post(`/users/${selectedSubAccount.id}/resend-invite`, { channels });
      const delivery = res.data?.invite_delivery || {};
      const sentKeys = Object.entries(delivery).filter(([, s]) => s === 'sent').map(([c]) => c);
      setSelectedSubAccount(prev => ({
        ...prev,
        invite_status: 'pending_invitation',
        ...(sentKeys.length ? { invite_last_sent_at: new Date().toISOString(), invite_last_channels: sentKeys } : {}),
      }));
      const sentChans = sentKeys.map((c) => (c === 'sms' ? 'SMS' : 'Email'));
      if (sentChans.length) {
        toast.success(`Invitation re-sent via ${sentChans.join(' & ')}`);
      } else {
        toast.warning('Invitation link regenerated, but nothing was delivered. Tick a channel above or use "Copy activation link".');
      }
      if (resendSubInviteSms && delivery.sms && delivery.sms !== 'sent') {
        const smsMsg = delivery.sms === 'not_configured'
          ? 'SMS not sent: no SMS provider is configured (contact your administrator).'
          : delivery.sms === 'no_recipient'
            ? 'SMS not sent: no mobile number on file (add one above and save).'
            : delivery.sms === 'not_allowed'
              ? 'SMS not sent: your SMS notification privilege is off.'
              : 'SMS delivery failed — try again or copy the link.';
        toast.warning(smsMsg);
      }
      fetchSubAccounts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to resend invitation');
    } finally {
      setSubInviteLoading(false);
    }
  };

  const handleCopySubAccountInviteLink = async () => {
    if (!selectedSubAccount) return;
    setSubInviteLoading(true);
    try {
      const res = await api.post(`/users/${selectedSubAccount.id}/resend-invite?send_email=false`);
      setSelectedSubAccount(prev => ({ ...prev, invite_status: 'pending_invitation' }));
      const url = res.data?.invite_url;
      if (url) {
        await navigator.clipboard.writeText(url);
        toast.success('Activation link copied to clipboard');
      }
      fetchSubAccounts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to generate activation link');
    } finally {
      setSubInviteLoading(false);
    }
  };

  const handleCancelSubAccountInvite = () => {
    if (!selectedSubAccount) return;
    setPendingSettingsAction({
      title: 'Cancel Invitation',
      message: `Cancel the pending invitation for "${selectedSubAccount.full_name || selectedSubAccount.email}"? The activation link we sent will stop working immediately. The account is kept — you can correct the email and resend a fresh invitation afterwards.`,
      action: async () => {
        setSubInviteLoading(true);
        try {
          await api.post(`/users/${selectedSubAccount.id}/cancel-invite`);
          setSelectedSubAccount(prev => ({ ...prev, invite_status: 'cancelled' }));
          toast.success('Invitation cancelled — the activation link has been revoked');
          fetchSubAccounts();
        } catch (error) {
          const detail = error.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : 'Failed to cancel invitation');
        } finally {
          setSubInviteLoading(false);
        }
      },
    });
  };


  const handleUpdateSubAccount = async () => {
    if (!selectedSubAccount) return;
    
    try {
      // Update user details
      const updateData = {
        full_name: subAccountForm.full_name,
        is_active: subAccountForm.is_active,
        expiry_enabled: subAccountForm.expiry_enabled,
        // Only send expiry_date if it has a value, otherwise send null
        expiry_date: subAccountForm.expiry_date ? subAccountForm.expiry_date : null,
      };
      
      // Include username if provided
      if (subAccountForm.username) {
        updateData.username = subAccountForm.username;
      }
      
      // Only include password if provided
      if (subAccountForm.password) {
        updateData.password = subAccountForm.password;
      }
      
      await api.put(`/users/${selectedSubAccount.id}`, updateData);
      
      // Update features and devices
      await api.put(`/users/${selectedSubAccount.id}/features`, {
        features: subAccountForm.selected_features,
        assigned_device_ids: subAccountForm.selected_device_ids,
        assigned_person_ids: subAccountForm.selected_person_ids,
      });
      
      toast.success('Sub Account updated successfully');
      setIsEditSubAccountDialogOpen(false);
      setSelectedSubAccount(null);
      setSubAccountForm({ ...EMPTY_SUB_ACCOUNT_FORM });
      fetchSubAccounts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to update Sub Account';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      }
      
      toast.error(errorMessage);
    }
  };

  // Privilege tree structure for Sub Account page access (matches Admin Panel style).
  // Iter470: Mirrors the Admin Panel privilegesTree so the End User can grant every
  // page/subpage they themselves have — not just a hand-picked subset. Sub-account
  // features are additionally clamped by the parent (iter469 backend enforcement).
  const privilegesTree = [
    {
      id: 'dashboard_page',
      label: 'Dashboard',
      children: []
    },
    {
      id: 'live_tracking_page',
      label: 'Live Tracking',
      children: [
        { id: 'live_tracking_driver', label: 'Driver' },
        { id: 'live_tracking_staff', label: 'Staff' },
        { id: 'live_tracking_passengers', label: 'Passengers' },
        { id: 'live_tracking_assets', label: 'Trailer / Equipment' },
        { id: 'live_tracking_telemetry_data', label: 'Telemetry Data' },
        { id: 'live_tracking_commands', label: 'Commands' },
        { id: 'live_tracking_events', label: 'Events' },
      ]
    },
    {
      id: 'media_page',
      label: 'Media',
      children: [
        { id: 'media_event_videos', label: 'Event Videos' },
        { id: 'media_live_videos', label: 'Live Videos' },
        { id: 'media_remote_downloads', label: 'Remote Media' },
        { id: 'media_shared_evidence', label: 'Shared Evidence' },
      ]
    },
    {
      id: 'analytics_page',
      label: 'Analytics',
      children: [
        { id: 'analytics_reports', label: 'Reports' },
        { id: 'analytics_events', label: 'Events' },
        { id: 'analytics_alerts', label: 'Alerts' },
        { id: 'analytics_history', label: 'History' },
        { id: 'analytics_history_forensic_panel', label: 'History Forensic Panel' },
        { id: 'analytics_investigations', label: 'Investigations' },
      ]
    },
    {
      id: 'geofencing_page',
      label: 'Geofencing',
      children: [
        { id: 'geofencing_zones', label: 'Zones' },
        { id: 'geofencing_places', label: 'Places' },
        { id: 'geofencing_routes', label: 'Routes' },
      ]
    },
    {
      id: 'people_page',
      label: 'People',
      children: [
        { id: 'people_drivers', label: 'Drivers' },
        { id: 'people_staff', label: 'Staff' },
        { id: 'people_passengers', label: 'Passengers' },
        { id: 'people_rfid_ibutton_cards', label: 'RFID / iButton Cards' },
        { id: 'people_driver_corrections', label: 'Driver Corrections' },
      ]
    },
    {
      id: 'fleet_page',
      label: 'Fleet',
      children: [
        { id: 'fleet_vehicles', label: 'Vehicles' },
        { id: 'fleet_trailers', label: 'Trailers' },
        { id: 'fleet_maintenance', label: 'Maintenance' },
        { id: 'fleet_fuel', label: 'Fuel' },
        { id: 'fleet_equipment', label: 'Equipment' },
        { id: 'fleet_green_driving', label: 'Green Driving' },
      ]
    },
    {
      id: 'operations_page',
      label: 'Operations',
      children: [
        { id: 'operations_asset_booking', label: 'Asset Booking' },
        { id: 'operations_pre_checklist', label: 'Pre-Checklist' },
        { id: 'operations_reporting', label: 'Reporting & Compliance' },
      ]
    },
    {
      id: 'chat_page',
      label: 'Chat',
      children: []
    },
    {
      id: 'installation_page',
      label: 'Installation',
      children: []
    },
    {
      id: 'settings_page',
      label: 'Settings',
      children: [
        { id: 'settings_general', label: 'General' },
        { id: 'settings_notifications', label: 'Notifications' },
        { id: 'settings_integrations', label: 'Integrations' },
        { id: 'settings_activity_trail', label: 'Activity Trail' },
        // settings_sub_accounts is intentionally omitted — Sub Accounts cannot
        // create Sub-sub-accounts (backend hardcodes this restriction).
      ]
    },
  ];

  // Filter the privileges tree to only show items the End User has access to
  const getFilteredPrivilegesTree = () => {
    return privilegesTree
      .filter(node => myFeatures[node.id] === true) // Only show pages End User has
      .map(node => ({
        ...node,
        children: node.children.filter(child => myFeatures[child.id] === true) // Only show subpages End User has
      }));
  };

  // Map providers and features (shown separately like Admin Panel)
  const mapProviders = [
    { id: 'osm_maps', label: 'OSM Maps' },
    { id: 'google_maps', label: 'Google Maps' },
  ];

  const mapFeatures = [
    { id: 'map_satellite_view', label: 'Satellite View' },
    { id: 'map_street_view', label: 'Google Street View (Future)' },
    { id: 'map_traffic_layer', label: 'Traffic Layer' },
    { id: 'map_terrain_view', label: 'Terrain View' },
  ];

  const mapAdvancedFeatures = [
    { id: 'map_heatmap', label: 'Heat Map' },
    { id: 'map_weather', label: 'Weather Overlay' },
    { id: 'map_nearest_device', label: 'Nearest Device Finder' },
    { id: 'map_dark_mode', label: 'Dark Mode Map' },
    { id: 'map_geofence_overlay', label: 'Geofence Overlay' },
    { id: 'google_directions_eta', label: 'Google Directions ETA' },
  ];

  // Iter470: additional privilege sections — the layout MIRRORS the Admin Panel > User
  // Management > Privileges tab exactly (same group titles, same descriptions, same 2-col
  // grid) so the two UIs feel identical. Each group only renders when the End User has
  // at least ONE granted feature within it — backend iter469 enforces the subset rule
  // permanently so this is purely a UX filter.
  const additionalPrivilegeGroups = [
    {
      key: 'device_edit_options',
      label: 'Device Edit Options',
      description: 'Control which device properties this sub account can edit from the Live Tracking page.',
      items: [
        { id: 'live_tracking_edit_asset', label: 'Edit Asset Type' },
        { id: 'live_tracking_edit_icon', label: 'Edit Device Icon' },
      ],
    },
    {
      key: 'more_options',
      label: 'More Options',
      description: 'Control access to sharing and tracking link features from the Live Tracking "More" menu.',
      items: [
        { id: 'share_position', label: 'Share Position' },
        { id: 'generate_link', label: 'Generate Link' },
      ],
    },
    {
      key: 'map_enhancement_features',
      label: 'Map Enhancement Features',
      description: 'Control access to advanced map features available in the Live Tracking "More Features" panel.',
      items: [
        { id: 'trip_direction_trail', label: 'Trip Direction Trail' },
        { id: 'animated_movement', label: 'Animated Movement' },
        { id: 'live_stop_timer', label: 'Live Stop Timer' },
        { id: 'device_card_customization', label: 'Device Card Customization' },
        { id: 'speed_alert_config', label: 'Advanced Road Speed Alert' },
        { id: 'snap_to_road', label: 'Snap to Road' },
        { id: 'osrm_route_eta', label: 'OSRM Route ETA' },
        { id: 'google_reverse_geocoding', label: 'Google Reverse Geocoding' },
      ],
    },
    {
      key: 'notification_channels',
      label: 'Notification Channels',
      description: 'Grant permission for this sub account to send and receive notifications via these channels. These settings are reflected in the user\'s Settings > Notifications page.',
      items: [
        { id: 'notification_email', label: 'Email' },
        { id: 'notification_sms', label: 'SMS' },
        { id: 'notification_push', label: 'Push Notifications' },
        { id: 'event_webhook_commands', label: 'Event Webhooks & Commands' },
      ],
    },
    {
      key: 'media',
      label: 'Media',
      description: 'Premium video features for Event Videos and Remote Media — trail playback inside the modal, and tokenised public evidence sharing.',
      items: [
        { id: 'media_video_show_trail_map', label: 'Show Trail Map (Event Videos)' },
        { id: 'media_share_event_evidence', label: 'Share Event Evidence Link' },
      ],
    },
  ];

  // Helper to handle privilege toggle with auto-select parent and auto-deselect children
  const handlePrivilegeToggle = (nodeId) => {
    const currentValue = subAccountForm.selected_features[nodeId] === true;
    const newValue = !currentValue;
    
    const newFeatures = {
      ...subAccountForm.selected_features,
      [nodeId]: newValue
    };
    
    if (newValue) {
      // CHECKING - If this is a subpage, also check the parent page
      for (const parentNode of privilegesTree) {
        if (parentNode.children && parentNode.children.some(child => child.id === nodeId)) {
          newFeatures[parentNode.id] = true;
          break;
        }
      }
    } else {
      // UNCHECKING - If unchecking a parent node, also uncheck all its children
      const node = privilegesTree.find(n => n.id === nodeId);
      if (node && node.children) {
        node.children.forEach(child => {
          newFeatures[child.id] = false;
        });
      }
      
      // Special case: if unchecking google_maps, also uncheck map features and advanced features
      if (nodeId === 'google_maps') {
        mapFeatures.forEach(f => {
          newFeatures[f.id] = false;
        });
        mapAdvancedFeatures.forEach(f => {
          newFeatures[f.id] = false;
        });
      }
      
      // Special case: if unchecking live_tracking_page, also uncheck map providers, features, and advanced
      if (nodeId === 'live_tracking_page') {
        mapProviders.forEach(p => {
          newFeatures[p.id] = false;
        });
        mapFeatures.forEach(f => {
          newFeatures[f.id] = false;
        });
        mapAdvancedFeatures.forEach(f => {
          newFeatures[f.id] = false;
        });
      }
    }
    
    setSubAccountForm({
      ...subAccountForm,
      selected_features: newFeatures
    });
  };

  // Iter470: renderer for the additional-privilege groups (Device Edit Options / More
  // Options / Map Enhancement / Notification Channels / Media). Visual style matches
  // the Admin Panel exactly. Only shows groups where the End User has at least ONE
  // granted feature in that group.
  const renderAdditionalPrivilegeGroups = () => (
    additionalPrivilegeGroups.map((group) => {
      const visibleItems = group.items.filter((item) => myFeatures[item.id] === true);
      if (visibleItems.length === 0) return null;
      return (
        <div key={group.key} className="pt-4 border-t" data-testid={`sub-account-privilege-group-${group.key}`}>
          <h3 className="font-semibold mb-3">{group.label}</h3>
          {group.description && (
            <p className="text-xs text-muted-foreground mb-3">{group.description}</p>
          )}
          <div className="grid grid-cols-2 gap-2">
            {visibleItems.map((item) => (
              <CheckboxOption
                key={item.id}
                checked={subAccountForm.selected_features[item.id] === true}
                onChange={(checked) => setSubAccountForm({
                  ...subAccountForm,
                  selected_features: { ...subAccountForm.selected_features, [item.id]: checked }
                })}
                label={item.label}
                testId={`sub-account-privilege-additional-${item.id}`}
              />
            ))}
          </div>
        </div>
      );
    })
  );

  // Iter470 v2: shared renderer for the FULL Sub Account "Privileges" tab body — used
  // by both the Add and Edit dialogs so the two never drift out of sync. Visual layout
  // MIRRORS the Admin Panel > User Management > Privileges tab exactly (same
  // <h3>/<p> heading style, same 2-col grid, same pt-4/border-t rhythm) so the two
  // UIs feel identical. Only shows groups where the End User has at least ONE
  // granted feature in that group.
  // ── Sub Account People allocation (Phase B) ──
  const [ownerPeople, setOwnerPeople] = useState(null); // null = not loaded
  const [peopleSearchFilter, setPeopleSearchFilter] = useState('');
  const [peopleTypeFilter, setPeopleTypeFilter] = useState('all');
  useEffect(() => {
    if ((isAddSubAccountDialogOpen || isEditSubAccountDialogOpen) && user?.role === 'end_user') {
      Promise.all([
        api.get('/drivers').catch(() => ({ data: [] })),
        api.get('/staff').catch(() => ({ data: [] })),
        api.get('/passengers').catch(() => ({ data: [] })),
      ]).then(([d, s, p]) => {
        const mk = (rows, type) => (Array.isArray(rows) ? rows : []).map((r) => ({
          id: r.id, name: r.full_name || r.name || r.email || r.id, type,
        }));
        setOwnerPeople([...mk(d.data, 'Driver'), ...mk(s.data, 'Staff'), ...mk(p.data, 'Passenger')]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddSubAccountDialogOpen, isEditSubAccountDialogOpen]);

  const filteredOwnerPeople = (ownerPeople || []).filter((p) => {
    if (peopleTypeFilter !== 'all' && p.type !== peopleTypeFilter) return false;
    const q = peopleSearchFilter.trim().toLowerCase();
    return !q || p.name.toLowerCase().includes(q);
  });

  const togglePersonAllocation = (personId, checked) => {
    setSubAccountForm((prev) => ({
      ...prev,
      selected_person_ids: checked
        ? [...new Set([...(prev.selected_person_ids || []), personId])]
        : (prev.selected_person_ids || []).filter((id) => id !== personId),
    }));
  };

  const renderSubAccountPeopleTabBody = () => (
    <div className="h-full flex flex-col space-y-4" data-testid="subaccount-people-tab-body">
      <div>
        <Label className="text-base font-semibold">People Access</Label>
        <p className="text-xs text-muted-foreground">
          Select which of your People (Drivers, Staff, Passengers) this sub account can see and manage.
          No selection means the sub account sees no People, even with People privileges enabled.
        </p>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search people..."
            value={peopleSearchFilter}
            onChange={(e) => setPeopleSearchFilter(e.target.value)}
            className="h-8 pl-10"
            data-testid="subaccount-people-search"
          />
        </div>
        <Select value={peopleTypeFilter} onValueChange={setPeopleTypeFilter}>
          <SelectTrigger className="w-[140px] h-8" data-testid="subaccount-people-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Driver">Drivers</SelectItem>
            <SelectItem value="Staff">Staff</SelectItem>
            <SelectItem value="Passenger">Passengers</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="subaccount-people-count">
          {(subAccountForm.selected_person_ids || []).length} of {(ownerPeople || []).length} allocated
        </span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs"
            onClick={() => setSubAccountForm((prev) => ({
              ...prev,
              selected_person_ids: [...new Set([...(prev.selected_person_ids || []), ...filteredOwnerPeople.map((p) => p.id)])],
            }))}
            data-testid="subaccount-people-select-all">
            Select Visible
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs"
            onClick={() => setSubAccountForm((prev) => ({ ...prev, selected_person_ids: [] }))}
            data-testid="subaccount-people-clear">
            Clear
          </Button>
        </div>
      </div>
      {ownerPeople === null ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : filteredOwnerPeople.length > 0 ? (
        <div className="border rounded-lg flex-1 min-h-0 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-28">Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOwnerPeople.map((p) => (
                <TableRow key={p.id} data-testid={`subaccount-person-row-${p.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={(subAccountForm.selected_person_ids || []).includes(p.id)}
                      onCheckedChange={(checked) => togglePersonAllocation(p.id, !!checked)}
                      data-testid={`subaccount-person-checkbox-${p.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-sm">{p.name}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-[10px]">{p.type}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-10">
          {(ownerPeople || []).length === 0 ? 'You have no People to allocate yet.' : 'No people match your filter.'}
        </p>
      )}
    </div>
  );

  const renderSubAccountPrivilegesTabBody = () => (
    <div className="space-y-6">
      {/* Page & Module Access — the top tree, still boxed like the Admin Panel tree */}
      <div className="space-y-3">
        <h3 className="font-semibold">Page & Module Access</h3>
        <p className="text-xs text-muted-foreground">Select which pages and features this sub account can access</p>
        <div className="border rounded-lg p-4">
          {getFilteredPrivilegesTree().map((node) => (
            <PrivilegeTreeNode
              key={node.id}
              node={node}
              selectedFeatures={subAccountForm.selected_features}
              onToggle={handlePrivilegeToggle}
            />
          ))}
        </div>
      </div>

      {/* Map Provider Access */}
      {myFeatures['live_tracking_page'] && mapProviders.some(p => myFeatures[p.id] === true) && (
        <div className="pt-4 border-t">
          <h3 className="font-semibold mb-3">Map Provider Access</h3>
          <p className="text-xs text-muted-foreground mb-3">OSM Maps will be shown as default if no map providers are selected</p>
          <div className="space-y-2">
            {mapProviders.filter(p => myFeatures[p.id] === true).map((provider) => (
              <CheckboxOption
                key={provider.id}
                bordered={false}
                checked={subAccountForm.selected_features[provider.id] === true}
                onChange={() => handlePrivilegeToggle(provider.id)}
                label={provider.label}
              />
            ))}
          </div>
        </div>
      )}

      {/* Google Maps Features */}
      {myFeatures['google_maps'] && subAccountForm.selected_features['google_maps'] && mapFeatures.some(f => myFeatures[f.id] === true) && (
        <div className="pt-4 border-t">
          <h3 className="font-semibold mb-3">Google Maps Features</h3>
          <p className="text-xs text-muted-foreground mb-3">Enable additional Google Maps layers and views</p>
          <div className="grid grid-cols-2 gap-2">
            {mapFeatures.filter(f => myFeatures[f.id] === true).map((feature) => (
              <CheckboxOption
                key={feature.id}
                checked={subAccountForm.selected_features[feature.id] === true}
                onChange={(checked) => setSubAccountForm({ ...subAccountForm, selected_features: { ...subAccountForm.selected_features, [feature.id]: checked } })}
                label={feature.label}
              />
            ))}
          </div>
        </div>
      )}

      {/* Map Advanced Features */}
      {myFeatures['google_maps'] && subAccountForm.selected_features['google_maps'] && mapAdvancedFeatures.some(f => myFeatures[f.id] === true) && (
        <div className="pt-4 border-t">
          <h3 className="font-semibold mb-3">Map Advanced Features</h3>
          <p className="text-xs text-muted-foreground mb-3">Enable advanced map overlays and tools</p>
          <div className="grid grid-cols-2 gap-2">
            {mapAdvancedFeatures.filter(f => myFeatures[f.id] === true).map((feature) => (
              <CheckboxOption
                key={feature.id}
                checked={subAccountForm.selected_features[feature.id] === true}
                onChange={(checked) => setSubAccountForm({ ...subAccountForm, selected_features: { ...subAccountForm.selected_features, [feature.id]: checked } })}
                label={feature.label}
              />
            ))}
          </div>
        </div>
      )}

      {/* Additional privilege groups — Admin Panel style (Device Edit / More Options /
          Map Enhancement / Notification Channels / Media) */}
      {renderAdditionalPrivilegeGroups()}
    </div>
  );

  const handleDeleteSubAccount = async (subAccountId) => {
    setPendingSettingsAction({ title: 'Delete Sub Account?', requireTyped: true, message: 'Are you sure you want to delete this Sub Account? This action cannot be undone.', action: async () => {
      try { await api.delete(`/users/${subAccountId}`); toast.success('Sub Account deleted'); fetchSubAccounts(); }
      catch (error) { toast.error('Failed to delete Sub Account'); }
    }});
  };

  // Fetch a Sub Account's audit trail (for the Audit tab in the Edit dialog)
  const fetchSubAccountAudit = useCallback(async (subAccountId) => {
    if (!subAccountId) return;
    setSubAccountAuditLoading(true);
    try {
      const res = await api.get(`/users/${subAccountId}/audit?limit=100`);
      setSubAccountAuditLogs(res.data?.logs || []);
    } catch (error) {
      setSubAccountAuditLogs([]);
    } finally {
      setSubAccountAuditLoading(false);
    }
  }, [api]);

  // Timezone-aware timestamp formatter for the Sub Account audit list (audit policy:
  // always include the exact UTC moment next to the local time).
  const formatSubAccountTimestamp = useCallback((ts) => (
    formatUserDateTimeWithUTC(ts, generalSettings.date_format, generalSettings.time_format, generalSettings.timezone)
  ), [generalSettings.date_format, generalSettings.time_format, generalSettings.timezone]);

  // Login as Sub Account - uses impersonate endpoint
  const handleLoginAsSubAccount = async (subAccount) => {
    try {
      const response = await api.post(`/users/${subAccount.id}/impersonate`);
      const { token, user: impersonatedUser, impersonated_by_name, impersonation_session_id, started_at } = response.data;
      
      // Open new window with the impersonated session using sessionStorage for isolation
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Logging in as ${impersonatedUser.full_name}...</title>
            <style>
              body { font-family: system-ui; display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; background: #f5f5f5; }
              .loader { text-align: center; }
              .spinner { width: 40px; height: 40px; border: 3px solid #e5e5e5; border-top-color: #2563eb; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px; }
              @keyframes spin { to { transform: rotate(360deg); } }
            </style>
          </head>
          <body>
            <div class="loader">
              <div class="spinner"></div>
              <p>Logging in as <strong>${impersonatedUser.full_name}</strong>...</p>
            </div>
            <script>
              // Store in sessionStorage for tab-level isolation (doesn't affect main tab)
              sessionStorage.setItem('impersonate_token', '${token}');
              sessionStorage.setItem('impersonate_user', '${JSON.stringify(impersonatedUser).replace(/'/g, "\\'")}');
              sessionStorage.setItem('impersonate_by_name', '${String(impersonated_by_name || '').replace(/'/g, "\\'")}');
              sessionStorage.setItem('impersonate_session_id', '${impersonation_session_id || ''}');
              sessionStorage.setItem('impersonate_started_at', '${started_at || ''}');
              setTimeout(() => { window.location.href = '/'; }, 500);
            </script>
          </body>
          </html>
        `);
        newWindow.document.close();
        toast.success(`Opened new window as ${impersonatedUser.full_name}`);
      } else {
        toast.error('Please allow popups to login as another user');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as sub account');
    }
  };

  // Generate Quick Login Link for Sub Account
  const handleGenerateQuickLogin = async () => {
    if (!selectedSubAccount) return;
    
    setQuickLoginLoading(true);
    try {
      const response = await api.post(`/users/${selectedSubAccount.id}/quick-login-token`);
      const token = response.data.quick_login_token;
      const url = `${window.location.origin}/quick-login/${token}`;
      setQuickLoginUrl(url);
      
      // Update the selected sub account with the new token info
      setSelectedSubAccount(prev => ({
        ...prev,
        quick_login_token: token
      }));
      
      toast.success('Quick login link generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate quick login link');
    } finally {
      setQuickLoginLoading(false);
    }
  };

  // Revoke Quick Login Link
  const handleRevokeQuickLogin = async () => {
    if (!selectedSubAccount) return;
    
    setQuickLoginLoading(true);
    try {
      await api.delete(`/users/${selectedSubAccount.id}/quick-login-token`);
      setQuickLoginUrl('');
      setSelectedSubAccount(prev => ({
        ...prev,
        quick_login_token: null
      }));
      toast.success('Quick login link revoked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke quick login link');
    } finally {
      setQuickLoginLoading(false);
    }
  };

  // Copy Quick Login URL to clipboard
  const copyQuickLoginUrl = () => {
    if (quickLoginUrl) {
      navigator.clipboard.writeText(quickLoginUrl);
      toast.success('Link copied to clipboard');
    }
  };

  // Filter sub accounts based on search query
  const filteredSubAccounts = subAccounts.filter(sa => {
    const matchesSearch = (
      sa.full_name.toLowerCase().includes(subAccountSearchQuery.toLowerCase()) ||
      sa.email.toLowerCase().includes(subAccountSearchQuery.toLowerCase()) ||
      (sa.username && sa.username.toLowerCase().includes(subAccountSearchQuery.toLowerCase()))
    );
    if (!matchesSearch) return false;
    if (subAccountPendingOnly && sa.invite_status !== 'pending_invitation' && sa.invite_status !== 'expired') return false;
    return true;
  });
  const pagedSubAccounts = paginateArray(filteredSubAccounts, subAccountsPage, subAccountsPageSize);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Page Header */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <SettingsIcon className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground">Configure platform settings, notifications, and integrations</p>
        </div>
      </div>

      {/* Sub-navigation Tabs */}
      <MobileSectionMenu
        items={[
          hasGeneral && { id: 'general', label: 'General', icon: Globe },
          hasNotifications && { id: 'notifications', label: 'Notifications', icon: Bell },
          hasIntegrations && { id: 'integrations', label: 'Integrations', icon: LinkIcon },
          hasSubAccounts && { id: 'sub_accounts', label: 'Sub Accounts', icon: Users },
          hasActivity && { id: 'activity', label: 'Activity', icon: HistoryIcon },
        ].filter(Boolean)}
        activeId={activeTab}
        onChange={handleSettingsTabChange}
        testIdPrefix="settings-tabs"
      />
      <div className="hidden sm:flex gap-2 flex-wrap">
        {hasGeneral && (
          <Button
            onClick={() => handleSettingsTabChange('general')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'general' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-general"
          >
            <Globe className="w-4 h-4" />
            General
          </Button>
        )}
        {hasNotifications && (
          <Button
            onClick={() => handleSettingsTabChange('notifications')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'notifications' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-notifications"
          >
            <Bell className="w-4 h-4" />
            Notifications
          </Button>
        )}
        {hasIntegrations && (
          <Button
            onClick={() => handleSettingsTabChange('integrations')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'integrations' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-integrations"
          >
            <LinkIcon className="w-4 h-4" />
            Integrations
          </Button>
        )}
        {hasSubAccounts && (
          <Button
            onClick={() => handleSettingsTabChange('sub_accounts')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'sub_accounts' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-sub-accounts"
          >
            <Users className="w-4 h-4" />
            Sub Accounts
          </Button>
        )}
        {hasActivity && (
          <Button
            onClick={() => handleSettingsTabChange('activity')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'activity' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-activity"
          >
            <HistoryIcon className="w-4 h-4" />
            Activity
          </Button>
        )}
      </div>

      {/* No Access Message - SECURITY FIX */}
      {!activeTab && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">No Access</p>
            <p className="text-sm text-muted-foreground mt-2">You don&apos;t have permission to access any sections of Settings.</p>
            <p className="text-xs text-muted-foreground mt-4">Contact your administrator to request access.</p>
          </CardContent>
        </Card>
      )}

      {/* ==================== GENERAL TAB ==================== */}
      {activeTab === 'general' && hasGeneral && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-foreground" />
              <h2 className="text-xl font-semibold">General Settings</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Configure localization, units, and display defaults for your account.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Units & Measurement */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Units & Measurement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Distance Unit</Label>
                  <Select
                    value={generalSettings.distance_unit}
                    onValueChange={(v) => setGeneralSettings(prev => ({ ...prev, distance_unit: v }))}
                  >
                    <SelectTrigger className="dark:bg-[#172031]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="km">Kilometres (km)</SelectItem>
                      <SelectItem value="mi">Miles (mi)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Speed Unit</Label>
                  <Select
                    value={generalSettings.speed_unit}
                    onValueChange={(v) => setGeneralSettings(prev => ({ ...prev, speed_unit: v }))}
                  >
                    <SelectTrigger className="dark:bg-[#172031]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kmh">km/h</SelectItem>
                      <SelectItem value="mph">mph</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Time Zone</Label>
                  <TimezoneSelector
                    value={generalSettings.timezone}
                    onChange={(v) => setGeneralSettings(prev => ({ ...prev, timezone: v }))}
                    triggerClassName="dark:bg-[#172031]"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Date & Time Format */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Date & Time Format</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Date Format</Label>
                  <Select
                    value={generalSettings.date_format}
                    onValueChange={(v) => setGeneralSettings(prev => ({ ...prev, date_format: v }))}
                  >
                    <SelectTrigger className="dark:bg-[#172031]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (21/01/2026)</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (01/21/2026)</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2026-01-21)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Time Format</Label>
                  <Select
                    value={generalSettings.time_format}
                    onValueChange={(v) => setGeneralSettings(prev => ({ ...prev, time_format: v }))}
                  >
                    <SelectTrigger className="dark:bg-[#172031]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12h">12-hour (2:30 PM)</SelectItem>
                      <SelectItem value="24h">24-hour (14:30)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select
                    value={generalSettings.language}
                    onValueChange={(v) => setGeneralSettings(prev => ({ ...prev, language: v }))}
                  >
                    <SelectTrigger className="dark:bg-[#172031]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Driving Behaviour score rating thresholds — account-wide, hidden for People logins
              and gated behind the Green Driving feature flag / user privilege */}
          {user?.role !== 'chat_contact' && isSubpageEnabled('fleet_green_driving') && <BehaviourScoreSettingsCard api={api} />}

          {/* Data Retention Information Card — owner/tenant-level, hidden for People (chat_contact) logins */}
          {user?.role !== 'chat_contact' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-5 h-5" />
                Data Retention Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                <div>
                  <p className="font-semibold text-blue-900 dark:text-blue-300">Your Data Retention Period</p>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                    Operational history available to your account
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-600">{retentionPolicy.effective_operational_history_days}</p>
                  <p className="text-sm text-blue-700 dark:text-blue-400">days</p>
                </div>
              </div>
              {(isFeatureEnabled('chat_page') || isFeatureEnabled('media_page')) && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {isFeatureEnabled('chat_page') && (
                    <div className="flex items-center justify-between p-4 rounded-lg bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800" data-testid="retention-chat-box">
                      <div>
                        <p className="font-semibold text-violet-900 dark:text-violet-300">Chat Retention Period</p>
                        <p className="text-sm text-violet-700 dark:text-violet-400 mt-1">
                          Chat message history available to your account
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-violet-600" data-testid="retention-chat-days">{retentionPolicy.chat_messages_days}</p>
                        <p className="text-sm text-violet-700 dark:text-violet-400">days</p>
                      </div>
                    </div>
                  )}
                  {isFeatureEnabled('media_page') && (
                    <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800" data-testid="retention-video-box">
                      <div>
                        <p className="font-semibold text-emerald-900 dark:text-emerald-300">Video Retention Period</p>
                        <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">
                          Recorded videos & media available to your account
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-emerald-600" data-testid="retention-video-days">{retentionPolicy.video_metadata_days}</p>
                        <p className="text-sm text-emerald-700 dark:text-emerald-400">days</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-muted-foreground">
                <div className="rounded-lg border bg-muted/30 px-3 py-2">
                  Additional GPS history can be purchased for up to <span className="font-semibold text-foreground">{retentionPolicy.global_operational_history_days}</span> days.
                </div>
                <div className="rounded-lg border bg-muted/30 px-3 py-2">
                  Your account visibility is capped at <span className="font-semibold text-foreground">{retentionPolicy.user_history_retention_days}</span> days.
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                <Shield className="w-3 h-3 inline mr-1" />
                Contact your administrator to modify data retention settings
              </p>
            </CardContent>
          </Card>
          )}

          <div className="flex justify-end">
            <Button size="sm" onClick={handleSaveGeneral} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save General Settings
            </Button>
          </div>

          {/* Devices & Sign-in Security — active sessions + suspicious-login trust memory */}
          <DeviceSecurityCard api={api} />
        </div>
      )}

      {/* ==================== NOTIFICATIONS TAB ==================== */}
      {activeTab === 'notifications' && hasNotifications && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-foreground" />
              <h2 className="text-xl font-semibold">Notification Settings</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Control how alerts and notifications are delivered to you.
            </p>
          </div>

          {/* Notification Preferences grid — every category per channel, in one place */}
          <Card data-testid="notification-preferences-grid">
            <CardHeader>
              <CardTitle className="text-base">Notification Preferences</CardTitle>
              <CardDescription>
                Turn each notification category on or off per delivery channel. Detailed options
                (sounds, quiet hours, devices) are in the cards below.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground border-b">
                      <th className="text-left py-2 pr-2 font-medium">Category</th>
                      <th className="text-center py-2 px-2 font-medium">Bell / Toast</th>
                      <th className="text-center py-2 px-2 font-medium">Email</th>
                      <th className="text-center py-2 px-2 font-medium">Push</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2.5 pr-2">
                        <p className="font-medium">Event &amp; driving alerts</p>
                        <p className="text-xs text-muted-foreground">Geofences, speeding, device events from your event rules</p>
                      </td>
                      <td className="text-center py-2.5 px-2"><Badge variant="secondary" className="text-[10px]">Always on</Badge></td>
                      <td className="text-center py-2.5 px-2 text-muted-foreground">—</td>
                      <td className="text-center py-2.5 px-2">
                        <Switch
                          checked={accountPushEnabled}
                          onCheckedChange={handleAccountPushToggle}
                          disabled={!isFeatureEnabled('notification_push') || pushLoading}
                          data-testid="grid-events-push-toggle"
                        />
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2.5 pr-2">
                        <p className="font-medium">Chat messages</p>
                        <p className="text-xs text-muted-foreground">New chat messages sent to you</p>
                      </td>
                      <td className="text-center py-2.5 px-2"><Badge variant="secondary" className="text-[10px]">Always on</Badge></td>
                      <td className="text-center py-2.5 px-2 text-muted-foreground">—</td>
                      <td className="text-center py-2.5 px-2">
                        <Switch
                          checked={notificationSettings.chat_push_enabled !== false}
                          onCheckedChange={(v) => savePrefField('chat_push_enabled', v, 'Chat push')}
                          disabled={!isFeatureEnabled('notification_push') || !accountPushEnabled}
                          data-testid="grid-chat-push-toggle"
                        />
                      </td>
                    </tr>
                    {canReceiveDriverBallots && (
                    <tr className="border-b">
                      <td className="py-2.5 pr-2">
                        <p className="font-medium">Driver trip confirmations</p>
                        <p className="text-xs text-muted-foreground">&ldquo;Who is driving?&rdquo; ballots and confirmations</p>
                      </td>
                      <td className="text-center py-2.5 px-2">
                        <Switch
                          checked={notificationSettings.driver_confirmation_popup_enabled !== false}
                          onCheckedChange={(v) => savePrefField('driver_confirmation_popup_enabled', v, 'Driver confirmation pop-up')}
                          data-testid="grid-driver-toast-toggle"
                        />
                      </td>
                      <td className="text-center py-2.5 px-2 text-muted-foreground">—</td>
                      <td className="text-center py-2.5 px-2">
                        <Switch
                          checked={notificationSettings.driver_confirmation_push_enabled !== false}
                          onCheckedChange={(v) => savePrefField('driver_confirmation_push_enabled', v, 'Driver confirmation push')}
                          disabled={!isFeatureEnabled('notification_push') || !accountPushEnabled}
                          data-testid="grid-driver-push-toggle"
                        />
                      </td>
                    </tr>
                    )}
                    <tr>
                      <td className="py-2.5 pr-2">
                        <p className="font-medium">Security alerts</p>
                        <p className="text-xs text-muted-foreground">Your password changed, account accessed by an admin, suspicious or out-of-region sign-ins</p>
                      </td>
                      <td className="text-center py-2.5 px-2"><Badge variant="secondary" className="text-[10px]">Always on</Badge></td>
                      <td className="text-center py-2.5 px-2"><Badge variant="secondary" className="text-[10px]">Always on</Badge></td>
                      <td className="text-center py-2.5 px-2">
                        <Switch
                          checked={notificationSettings.security_alerts_push_enabled !== false}
                          onCheckedChange={(v) => savePrefField('security_alerts_push_enabled', v, 'Security alert push')}
                          disabled={!isFeatureEnabled('notification_push') || !accountPushEnabled}
                          data-testid="grid-security-push-toggle"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Bell and email stay on for security alerts so you always know when your account is changed or accessed.
              </p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Notification Channels */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notification Channels</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {user?.role !== 'chat_contact' && (<>
                <div className={`flex items-center justify-between p-3 rounded-lg ${isFeatureEnabled('notification_email') ? 'bg-muted' : 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900'}`}>
                  <div className="flex items-center gap-3">
                    <Mail className={`w-5 h-5 ${isFeatureEnabled('notification_email') ? 'text-muted-foreground' : 'text-red-400'}`} />
                    <div>
                      <p className="font-medium text-sm">Email Notifications</p>
                      <p className="text-xs text-muted-foreground">
                        {isFeatureEnabled('notification_email') ? 'Send alerts via email' : 'Not granted — contact your administrator'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.email_enabled}
                    onCheckedChange={(v) => setNotificationSettings(prev => ({ ...prev, email_enabled: v }))}
                    disabled={!isFeatureEnabled('notification_email')}
                  />
                </div>

                <div className={`flex items-center justify-between p-3 rounded-lg ${isFeatureEnabled('notification_sms') ? 'bg-muted' : 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900'}`}>
                  <div className="flex items-center gap-3">
                    <Phone className={`w-5 h-5 ${isFeatureEnabled('notification_sms') ? 'text-muted-foreground' : 'text-red-400'}`} />
                    <div>
                      <p className="font-medium text-sm">SMS Notifications</p>
                      <p className="text-xs text-muted-foreground">
                        {isFeatureEnabled('notification_sms') ? 'Send alerts via SMS' : 'Not granted — contact your administrator'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.sms_enabled}
                    onCheckedChange={(v) => setNotificationSettings(prev => ({ ...prev, sms_enabled: v }))}
                    disabled={!isFeatureEnabled('notification_sms')}
                  />
                </div>
                </>)}

                <div className={`rounded-lg border p-3 space-y-3 ${isFeatureEnabled('notification_push') ? 'bg-muted/50 border-border' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900'}`} data-testid="push-notification-controls-card">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Bell className={`w-5 h-5 ${isFeatureEnabled('notification_push') ? 'text-muted-foreground' : 'text-red-400'}`} />
                      <div>
                        <p className="font-medium text-sm" data-testid="push-account-label">Push notifications for this account</p>
                        <p className="text-xs text-muted-foreground" data-testid="push-account-description">
                          {!isFeatureEnabled('notification_push')
                            ? 'Not granted — contact your administrator'
                            : 'Turning this off unsubscribes every saved push device for this account'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isFeatureEnabled('notification_push') && accountPushEnabled ? (
                        <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" data-testid="push-account-active-badge">Account on</Badge>
                      ) : null}
                      {isFeatureEnabled('notification_push') && !accountPushEnabled ? (
                        <Badge variant="outline" className="text-[10px]" data-testid="push-account-off-badge">Account off</Badge>
                      ) : null}
                      <Switch
                        data-testid="push-notification-toggle"
                        checked={accountPushEnabled}
                        onCheckedChange={handleAccountPushToggle}
                        disabled={!isFeatureEnabled('notification_push') || pushLoading}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 rounded-lg bg-background/80 px-3 py-3" data-testid="push-current-device-control-row">
                    <div className="flex items-center gap-3 min-w-0">
                      <Smartphone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm" data-testid="push-device-label">This device subscribed</p>
                        <p className="text-xs text-muted-foreground" data-testid="push-device-description">
                          {!accountPushEnabled
                            ? 'Turn on account push first. Turning account push off removes all saved devices.'
                            : !isPushSupported()
                              ? 'Push notifications are not supported in this browser'
                              : pushPermission === 'denied'
                                ? 'Blocked by browser — update your browser notification settings'
                                : localSubscriptionNeedsResync
                                  ? 'This browser has an old local subscription. Turn this on to re-subscribe it to your account.'
                                  : currentDeviceRegistered
                                    ? 'This browser or phone is subscribed and can receive push alerts'
                                    : 'Subscribe only this browser or phone to receive push alerts'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {pushPermission === 'denied' && (
                        <Badge variant="destructive" className="text-[10px]" data-testid="push-device-blocked-badge">Blocked</Badge>
                      )}
                      {isFeatureEnabled('notification_push') && accountPushEnabled && currentDeviceRegistered && pushPermission === 'granted' && (
                        <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" data-testid="push-device-active-badge">Subscribed</Badge>
                      )}
                      {isFeatureEnabled('notification_push') && accountPushEnabled && !currentDeviceRegistered && pushPermission !== 'denied' && (
                        <Badge variant="outline" className="text-[10px]" data-testid="push-device-off-badge">Not subscribed</Badge>
                      )}
                      <Switch
                        data-testid="push-current-device-toggle"
                        checked={thisDeviceSubscribed}
                        onCheckedChange={handleCurrentDevicePushToggle}
                        disabled={!isFeatureEnabled('notification_push') || !accountPushEnabled || !isPushSupported() || pushPermission === 'denied' || pushLoading}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg bg-background/80 px-3 py-3" data-testid="chat-push-control-row">
                    <div className="flex items-center gap-3 min-w-0">
                      <MessageSquare className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">Chat notifications</p>
                        <p className="text-xs text-muted-foreground">
                          Receive a push when someone sends you a chat message. Disable to mute chat
                          alerts without affecting other notifications.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch
                        data-testid="chat-push-toggle"
                        checked={notificationSettings.chat_push_enabled !== false}
                        onCheckedChange={async (checked) => {
                          setNotificationSettings(prev => ({ ...prev, chat_push_enabled: checked }));
                          try {
                            await api.put('/settings/notifications', { chat_push_enabled: checked });
                            toast.success(checked ? 'Chat notifications enabled' : 'Chat notifications muted');
                          } catch (err) {
                            toast.error('Failed to save chat notification setting');
                            // Revert local state on failure
                            setNotificationSettings(prev => ({ ...prev, chat_push_enabled: !checked }));
                          }
                        }}
                        disabled={!isFeatureEnabled('notification_push') || !accountPushEnabled || pushLoading}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Driver Confirmation Alert Sound — plays on this device when a driver-
                confirmation ballot arrives. Each user picks their own tone in Settings. */}
            {canReceiveDriverBallots && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Driver Confirmation Alert Sound</CardTitle>
                <CardDescription>
                  Choose the tone your bell plays when a driver-confirmation notification arrives
                  (e.g. &ldquo;Are you driving X?&rdquo;).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <p className="font-medium text-sm">Play sound when a driver-confirmation notification arrives</p>
                    <p className="text-xs text-muted-foreground">Turn off to keep the bell badge silent for these notifications.</p>
                  </div>
                  <Switch
                    checked={notificationSettings.driver_confirmation_sound_enabled !== false}
                    onCheckedChange={(checked) => setNotificationSettings(prev => ({
                      ...prev, driver_confirmation_sound_enabled: checked,
                    }))}
                    data-testid="driver-confirmation-sound-toggle"
                  />
                </div>

                {notificationSettings.driver_confirmation_sound_enabled !== false && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-1">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Alert Tone</Label>
                      <div className="flex items-center gap-2">
                        <Select
                          value={notificationSettings.driver_confirmation_sound_file || 'default'}
                          onValueChange={(v) => setNotificationSettings(prev => ({
                            ...prev, driver_confirmation_sound_file: v,
                          }))}
                        >
                          <SelectTrigger className="flex-1 dark:bg-[#172031]" data-testid="driver-confirmation-sound-file-select">
                            <SelectValue placeholder="Choose a tone" />
                          </SelectTrigger>
                          <SelectContent>
                            {ALERT_TONES.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => playAlertTone(notificationSettings.driver_confirmation_sound_file || 'default')}
                          data-testid="driver-confirmation-sound-test-btn"
                        >
                          ▶ Test
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Duration</Label>
                      <Select
                        value={notificationSettings.driver_confirmation_sound_duration || 'once'}
                        onValueChange={(v) => setNotificationSettings(prev => ({
                          ...prev, driver_confirmation_sound_duration: v,
                        }))}
                      >
                        <SelectTrigger className="dark:bg-[#172031]" data-testid="driver-confirmation-sound-duration-select">
                          <SelectValue placeholder="Duration" />
                        </SelectTrigger>
                        <SelectContent>
                          {ALERT_DURATIONS.map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <p className="font-medium text-sm">System message</p>
                    <p className="text-xs text-muted-foreground">Show an on-screen system message with a Confirm button when a driver-confirmation arrives.</p>
                  </div>
                  <Switch
                    checked={notificationSettings.driver_confirmation_popup_enabled !== false}
                    onCheckedChange={(checked) => setNotificationSettings(prev => ({
                      ...prev, driver_confirmation_popup_enabled: checked,
                    }))}
                    data-testid="driver-confirmation-popup-toggle"
                  />
                </div>

                {user?.role !== 'chat_contact' && (
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <p className="font-medium text-sm">Push notification</p>
                    <p className="text-xs text-muted-foreground">Send a browser push notification — works even when this tab is closed. Tapping it opens the Confirm Driver dialog.</p>
                  </div>
                  <Switch
                    checked={notificationSettings.driver_confirmation_push_enabled !== false}
                    onCheckedChange={(checked) => setNotificationSettings(prev => ({
                      ...prev, driver_confirmation_push_enabled: checked,
                    }))}
                    data-testid="driver-confirmation-push-toggle"
                  />
                </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Incoming Call Ringtone — per-user tone for WebRTC voice/video calls */}
            <Card data-testid="call-ringtone-card">
              <CardHeader>
                <CardTitle className="text-base">Incoming Call Ringtone</CardTitle>
                <CardDescription>
                  Choose the ringtone this account plays when a voice or video call comes in, or upload your own.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Select
                    value={notificationSettings.call_ringtone || 'classic'}
                    onValueChange={saveRingtoneChoice}
                  >
                    <SelectTrigger className="flex-1 dark:bg-[#172031]" data-testid="call-ringtone-select">
                      <SelectValue placeholder="Choose a ringtone" />
                    </SelectTrigger>
                    <SelectContent>
                      {CALL_RINGTONE_OPTIONS.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                      ))}
                      {notificationSettings.call_ringtone_name && (
                        <SelectItem value="custom">Custom — {notificationSettings.call_ringtone_name}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRingtonePreview}
                    data-testid="call-ringtone-test-btn"
                  >
                    ▶ Test
                  </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    ref={ringtoneFileInputRef}
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={handleRingtoneUpload}
                    data-testid="call-ringtone-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={ringtoneUploading}
                    onClick={() => ringtoneFileInputRef.current?.click()}
                    data-testid="call-ringtone-upload-btn"
                  >
                    {ringtoneUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload custom ringtone
                  </Button>
                  {notificationSettings.call_ringtone_name && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={handleRingtoneRemove}
                      data-testid="call-ringtone-remove-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Remove custom
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground w-full">MP3, OGG, WAV or M4A — up to 1MB. The ringtone loops while a call rings on this account.</p>
                </div>
              </CardContent>
            </Card>

            {/* Quiet Hours */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quiet Hours</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Enable Quiet Hours</p>
                      <p className="text-xs text-muted-foreground">Suppress non-critical alerts</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.quiet_hours_enabled}
                    onCheckedChange={(v) => setNotificationSettings(prev => ({ ...prev, quiet_hours_enabled: v }))}
                    data-testid="quiet-hours-enable-switch"
                  />
                </div>

                {notificationSettings.quiet_hours_enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{notificationSettings.quiet_hours_weekend_enabled ? 'Start Time (Mon–Fri)' : 'Start Time'}</Label>
                        <Input
                          type="time"
                          value={notificationSettings.quiet_hours_start}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                          className="dark:bg-[#172031]"
                          data-testid="quiet-hours-start-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{notificationSettings.quiet_hours_weekend_enabled ? 'End Time (Mon–Fri)' : 'End Time'}</Label>
                        <Input
                          type="time"
                          value={notificationSettings.quiet_hours_end}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                          className="dark:bg-[#172031]"
                          data-testid="quiet-hours-end-input"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Moon className="w-5 h-5 text-indigo-500" />
                        <div>
                          <p className="font-medium text-sm">Weekend Quiet Hours</p>
                          <p className="text-xs text-muted-foreground">Use a different window on Saturday &amp; Sunday — great for sleep-ins</p>
                        </div>
                      </div>
                      <Switch
                        checked={notificationSettings.quiet_hours_weekend_enabled}
                        onCheckedChange={(v) => setNotificationSettings(prev => ({ ...prev, quiet_hours_weekend_enabled: v }))}
                        data-testid="quiet-hours-weekend-switch"
                      />
                    </div>

                    {notificationSettings.quiet_hours_weekend_enabled && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Start Time (Sat–Sun)</Label>
                          <Input
                            type="time"
                            value={notificationSettings.quiet_hours_weekend_start}
                            onChange={(e) => setNotificationSettings(prev => ({ ...prev, quiet_hours_weekend_start: e.target.value }))}
                            className="dark:bg-[#172031]"
                            data-testid="quiet-hours-weekend-start-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Time (Sat–Sun)</Label>
                          <Input
                            type="time"
                            value={notificationSettings.quiet_hours_weekend_end}
                            onChange={(e) => setNotificationSettings(prev => ({ ...prev, quiet_hours_weekend_end: e.target.value }))}
                            className="dark:bg-[#172031]"
                            data-testid="quiet-hours-weekend-end-input"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        <div>
                          <p className="font-medium text-sm">Critical Alert Override</p>
                          <p className="text-xs text-muted-foreground">Allow critical alerts during quiet hours</p>
                        </div>
                      </div>
                      <Switch
                        checked={notificationSettings.critical_override}
                        onCheckedChange={(v) => setNotificationSettings(prev => ({ ...prev, critical_override: v }))}
                        data-testid="quiet-hours-critical-override-switch"
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card data-testid="push-subscription-list-card">
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-[#2563eb]" />
                    Active Push Devices
                  </CardTitle>
                  <CardDescription>
                    View which browsers and devices are currently subscribed for this account.
                  </CardDescription>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="push-device-list-summary-badges">
                    <Badge variant={accountPushEnabled ? 'secondary' : 'outline'} className={accountPushEnabled ? 'text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'text-[10px]'} data-testid="push-device-list-account-status-badge">
                      {accountPushEnabled ? 'Account delivery on' : 'Account delivery off'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]" data-testid="push-device-list-count-badge">
                      {pushSubscriptions.length} subscribed device{pushSubscriptions.length === 1 ? '' : 's'}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchPushSubscriptions}
                  disabled={loadingPushSubscriptions}
                  data-testid="refresh-push-subscriptions-btn"
                >
                  {loadingPushSubscriptions ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {loadingPushSubscriptions ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="push-subscriptions-loading">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading subscriptions...
                  </div>
                ) : pushSubscriptions.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="push-subscriptions-empty-state">
                    {!accountPushEnabled
                      ? 'Account push is off, so all saved device subscriptions were removed.'
                      : 'No active push subscriptions found for this account.'}
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="push-subscriptions-list">
                    {pushSubscriptions.map((sub) => {
                      const endpoint = sub.subscription_data?.endpoint || '';
                      const isCurrentDevice = currentPushEndpoint && endpoint === currentPushEndpoint;
                      const statusMeta = pushStatusMeta(sub.last_push_status);
                      return (
                        <div key={sub.id || endpoint} className="rounded-lg border bg-muted/20 p-3 space-y-2" data-testid={`push-subscription-${sub.id || 'unknown'}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{sub.device_label || 'Unknown device'}</p>
                              <p className="text-xs text-muted-foreground">{sub.platform || 'web'} subscription{sub.push_service ? ` • ${sub.push_service}` : ''}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-[10px]" data-testid={`push-subscription-delivery-badge-${sub.id || 'unknown'}`}>
                                {accountPushEnabled ? 'Delivery active' : 'Delivery off'}
                              </Badge>
                              <Badge variant="outline" className="text-[10px]" data-testid={`push-subscription-subscribed-badge-${sub.id || 'unknown'}`}>
                                Subscribed
                              </Badge>
                              <Badge className={statusMeta.className} data-testid={`push-subscription-status-badge-${sub.id || 'unknown'}`}>
                                {statusMeta.label}
                              </Badge>
                              {isCurrentDevice && (
                                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" data-testid={`push-subscription-current-badge-${sub.id || 'unknown'}`}>
                                  This device
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleRemovePushSubscription(endpoint)}
                                data-testid={`remove-push-subscription-btn-${sub.id || 'unknown'}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                            <div>
                              <span className="font-medium text-foreground">Subscribed:</span> {formatSubscriptionTime(sub.created_at)}
                            </div>
                            <div>
                              <span className="font-medium text-foreground">Updated:</span> {formatSubscriptionTime(sub.updated_at || sub.created_at)}
                            </div>
                            <div>
                              <span className="font-medium text-foreground">Last attempt:</span> {formatSubscriptionTime(sub.last_push_attempt_at)}
                            </div>
                            <div>
                              <span className="font-medium text-foreground">Last success:</span> {formatSubscriptionTime(sub.last_push_success_at)}
                            </div>
                            <div>
                              <span className="font-medium text-foreground">Last failure:</span> {formatSubscriptionTime(sub.last_push_failure_at)}
                            </div>
                            <div>
                              <span className="font-medium text-foreground">HTTP status:</span> {sub.last_push_http_status || '—'}
                            </div>
                          </div>
                          {sub.last_push_error ? (
                            <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] text-red-700 dark:border-red-900 dark:bg-red-950/20 dark:text-red-300" data-testid={`push-subscription-error-${sub.id || 'unknown'}`}>
                              <span className="font-medium">Last error:</span> {sub.last_push_error}
                            </div>
                          ) : null}
                          <p className="text-[11px] break-all text-muted-foreground" data-testid={`push-subscription-endpoint-${sub.id || 'unknown'}`}>
                            {formatEndpointPreview(endpoint)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end">
            <Button size="sm" onClick={handleSaveNotifications} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="notifications-save-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Notification Settings
            </Button>
          </div>
        </div>
      )}

      {/* ==================== INTEGRATIONS TAB ==================== */}
      {activeTab === 'integrations' && hasIntegrations && (
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-foreground" />
              <h2 className="text-xl font-semibold">Integrations</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Manage API keys, webhooks, and external integrations.
            </p>
          </div>

          {/* API Keys Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Key className="w-5 h-5" />
                API Keys
              </h3>
              <Button size="sm" onClick={() => setIsAddApiKeyDialogOpen(true)} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                <Plus className="w-4 h-4 mr-2" />
                Create API Key
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>API Key</TableHead>
                      <TableHead>Permissions</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Last Used</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apiKeys.map((key) => (
                      <TableRow key={key.id}>
                        <TableCell className="font-medium">{key.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                              {key.key}
                            </code>
                            <Button size="sm" variant="ghost">
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">{key.permissions}</Badge>
                        </TableCell>
                        <TableCell>{key.created_at}</TableCell>
                        <TableCell>{key.last_used}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-100 text-green-700 text-xs">
                            {key.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Webhooks Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Webhook className="w-5 h-5" />
                Webhooks
              </h3>
              <Button size="sm" onClick={() => setIsAddWebhookDialogOpen(true)} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                <Plus className="w-4 h-4 mr-2" />
                Add Webhook
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Endpoint URL</TableHead>
                      <TableHead>Events</TableHead>
                      <TableHead>Last Delivery</TableHead>
                      <TableHead>Success Rate</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {webhooks.map((webhook) => (
                      <TableRow key={webhook.id}>
                        <TableCell>
                          <code className="text-xs">{webhook.url}</code>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {webhook.events.map((event) => (
                              <Badge key={event} variant="outline" className="text-xs">
                                {event}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{webhook.last_delivery}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{webhook.success_rate}%</span>
                            {webhook.success_rate >= 95 ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-100 text-green-700 text-xs">
                            {webhook.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline">
                              <TestTube className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Add API Key Dialog */}
      <Dialog open={isAddApiKeyDialogOpen} onOpenChange={setIsAddApiKeyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>Generate a new API key for external integrations</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Key Name *</Label>
              <Input
                value={apiKeyForm.name}
                onChange={(e) => setApiKeyForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Mobile App Integration"
              />
            </div>

            <div className="space-y-2">
              <Label>Permissions</Label>
              <Select
                value={apiKeyForm.permissions}
                onValueChange={(v) => setApiKeyForm(prev => ({ ...prev, permissions: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read">Read Only</SelectItem>
                  <SelectItem value="full">Full Access</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>IP Allowlist (Optional)</Label>
              <Input
                value={apiKeyForm.ip_allowlist}
                onChange={(e) => setApiKeyForm(prev => ({ ...prev, ip_allowlist: e.target.value }))}
                placeholder="192.168.1.1, 10.0.0.1"
              />
              <p className="text-xs text-muted-foreground">Comma-separated IP addresses</p>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setIsAddApiKeyDialogOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreateApiKey} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Webhook Dialog */}
      <Dialog open={isAddWebhookDialogOpen} onOpenChange={setIsAddWebhookDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Webhook</DialogTitle>
            <DialogDescription>Configure webhook endpoint for real-time event delivery</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Endpoint URL *</Label>
              <Input
                value={webhookForm.url}
                onChange={(e) => setWebhookForm(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://api.example.com/webhooks/fleet"
              />
            </div>

            <div className="space-y-2">
              <Label>Webhook Secret</Label>
              <Input
                type="password"
                value={webhookForm.secret}
                onChange={(e) => setWebhookForm(prev => ({ ...prev, secret: e.target.value }))}
                placeholder="Your webhook secret for signature verification"
              />
            </div>

            <div className="space-y-2">
              <Label>Subscribe to Events</Label>
              <div className="grid grid-cols-2 gap-2">
                {['alerts', 'device_status', 'bookings', 'checklists', 'geofence', 'maintenance'].map((event) => (
                  <div key={event} className="flex items-center gap-2 p-2 rounded border">
                    <input
                      type="checkbox"
                      id={event}
                      className="rounded"
                      checked={webhookForm.events.includes(event)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setWebhookForm(prev => ({ ...prev, events: [...prev.events, event] }));
                        } else {
                          setWebhookForm(prev => ({ ...prev, events: prev.events.filter(e => e !== event) }));
                        }
                      }}
                    />
                    <label htmlFor={event} className="text-sm capitalize cursor-pointer">{event}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setIsAddWebhookDialogOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreateWebhook} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              Create Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== ACTIVITY TRAIL TAB ==================== */}
      {activeTab === 'activity' && hasActivity && (
        <ActivityTrailTab />
      )}

      {/* ==================== SUB ACCOUNTS TAB ==================== */}
      {activeTab === 'sub_accounts' && hasSubAccounts && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-foreground" />
              <h2 className="text-xl font-semibold">Sub Accounts</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Manage sub accounts that have access to your fleet data with limited permissions.
            </p>
          </div>

          {/* Toolbar row — limit chip on the left, search + actions on the right */}
          {/* Toolbar — Shared-Evidence-style: search input spans full width,
              then the action buttons. The former Sub Account Limit chip is
              replaced by a compact badge in the counts row below. */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search sub accounts..."
                value={subAccountSearchQuery}
                onChange={(e) => { setSubAccountSearchQuery(e.target.value); setSubAccountsPage(1); }}
                className="h-8 pl-10 pr-9"
                data-testid="sub-accounts-search"
              />
              {subAccountSearchQuery && (
                <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => { setSubAccountSearchQuery(''); setSubAccountsPage(1); }} data-testid="sub-accounts-clear-search">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={handleImportSubAccountsCSV}
              className="hidden"
              id="sub-account-csv-import"
            />
            <Button variant="outline" size="sm" onClick={() => document.getElementById('sub-account-csv-import').click()}>
              <Download className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportSubAccountsCSV} data-testid="export-subaccounts-btn" disabled={selectedSubAccountIds.length === 0}>
              <Upload className="w-4 h-4 mr-2" />
              {selectedSubAccountIds.length > 0 ? `Export (${selectedSubAccountIds.length})` : 'Export'}
            </Button>
            {selectedSubAccountIds.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleDeleteSelectedSubAccounts} data-testid="sub-accounts-bulk-delete-button">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedSubAccountIds.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={fetchSubAccounts}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button size="sm"
              className="gap-2 bg-[#2563eb] hover:bg-[#1d4ed8]"
              onClick={openAddSubAccountDialog}
              disabled={maxSubAccounts === 0 || (maxSubAccounts > 0 && subAccountsUnfilteredTotal >= maxSubAccounts)}
              title={maxSubAccounts === 0 ? 'Upgrade required — contact your administrator to add Sub Accounts.' : (maxSubAccounts > 0 && subAccountsUnfilteredTotal >= maxSubAccounts ? `Sub Account limit reached (${maxSubAccounts}). Contact your administrator to increase it.` : undefined)}
              data-testid="add-sub-account-button"
            >
              <Plus className="w-4 h-4" />
              Add Sub Account
            </Button>
          </div>

          {/* Counts row — compact badge for the Sub Account Limit, matches the
              Shared Evidence / Driver Corrections counts row pattern. */}
          {maxSubAccounts !== -1 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="sub-accounts-counts-row">
              <Badge
                variant={subAccountsUnfilteredTotal >= maxSubAccounts && maxSubAccounts > 0 ? 'destructive' : 'outline'}
                title={maxSubAccounts === 0 ? 'Contact your administrator to enable Sub Account creation.' : 'Sub Account Limit'}
                data-testid="sub-account-limit-badge"
              >
                Sub Account Limit {subAccountsUnfilteredTotal} / {maxSubAccounts === 0 ? 'Upgrade Required' : maxSubAccounts}
              </Badge>
            </div>
          )}

          {/* Selection bar — mirrors Shared Evidence: appears only when at least one
              row is ticked; Select All selects EVERY filtered row across all pages. */}
          {selectedSubAccountIds.length > 0 && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground px-1" data-testid="sub-accounts-selection-bar">
              <span data-testid="sub-accounts-selection-count">{selectedSubAccountIds.length} selected</span>
              <Button variant="ghost" size="sm" className="h-7 text-xs"
                data-testid="sub-accounts-select-all-toggle"
                disabled={subAccountsSelectAllLoading}
                onClick={async () => {
                  if (selectedSubAccountIds.length >= subAccountsTotal) { setSelectedSubAccountIds([]); return; }
                  setSubAccountsSelectAllLoading(true);
                  try {
                    const params = buildSubAccountParams();
                    params.set('limit', '10000');
                    const res = await api.get(`/users?${params.toString()}`);
                    setSelectedSubAccountIds((res.data || []).map(sa => sa.id).filter(Boolean));
                  } catch (error) {
                    toast.error('Failed to select all rows');
                  } finally {
                    setSubAccountsSelectAllLoading(false);
                  }
                }}
              >
                {subAccountsSelectAllLoading ? 'Selecting…' : (selectedSubAccountIds.length >= subAccountsTotal ? 'Deselect All' : 'Select All')}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedSubAccountIds([])} data-testid="sub-accounts-selection-clear">Clear</Button>
            </div>
          )}

          {/* Pending-invitations quick filter (mirrors Admin User Management) */}
          {(() => {
            const pendingCount = subAccountsPendingTotal;
            if (pendingCount === 0 && !subAccountPendingOnly) return null;
            return (
              <button
                type="button"
                onClick={() => { setSubAccountPendingOnly(v => !v); setSubAccountsPage(1); }}
                className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 transition-colors ${subAccountPendingOnly ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50'}`}
                title={subAccountPendingOnly ? 'Showing pending invitations — click to clear' : 'Show only pending invitations'}
                data-testid="sub-account-pending-invites-filter"
              >
                <Mail className="w-3.5 h-3.5" />
                {pendingCount} pending invitation{pendingCount === 1 ? '' : 's'}
                {subAccountPendingOnly && <X className="w-3 h-3 ml-0.5" />}
              </button>
            );
          })()}

          {/* Sub Accounts Table */}
          <Card>
            <CardContent className="p-0">
              {!subAccountsInitialLoadDone ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" />
                </div>
              ) : filteredSubAccounts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mb-3 opacity-50" />
                  <p className="text-muted-foreground">
                    {subAccountSearchQuery ? 'No sub accounts match your search' : 'No sub accounts yet'}
                  </p>
                  {!subAccountSearchQuery && maxSubAccounts !== 0 && (
                    <Button 
                      variant="outline" 
                      className="mt-4" 
                      onClick={openAddSubAccountDialog}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create your first Sub Account
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={pagedSubAccounts.length > 0 && pagedSubAccounts.every(sa => selectedSubAccountIds.includes(sa.id))}
                          onCheckedChange={handleToggleAllSubAccounts}
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedSubAccounts.map((sa) => (
                      <TableRow 
                        key={sa.id} 
                        className={`cursor-pointer hover:bg-muted/50 ${selectedSubAccountIds.includes(sa.id) ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}
                        onClick={() => handleEditSubAccount(sa)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedSubAccountIds.includes(sa.id)}
                            onCheckedChange={() => handleToggleSubAccountSelection(sa.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{sa.full_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{sa.username || '-'}</TableCell>
                        <TableCell className="text-muted-foreground">{sa.email}</TableCell>
                        <TableCell>
                          {(() => {
                            const expired = isSubAccountExpired(sa);
                            const isBlocked = sa.is_active === false || expired;
                            return (
                              <div className="flex flex-col items-start gap-1">
                                <div className="flex items-center gap-1">
                                  {isBlocked ? (
                                    <Badge variant="destructive">Blocked</Badge>
                                  ) : (
                                    <Badge className="bg-green-600">Active</Badge>
                                  )}
                                  {expired && sa.is_active !== false && (
                                    <span className="text-xs text-orange-600 dark:text-orange-400" title="Account expired - pending backend sync">
                                      (Expired)
                                    </span>
                                  )}
                                </div>
                                {sa.invite_status === 'pending_invitation' && (
                                  <Badge variant="outline" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 gap-1 cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/50" title="Awaiting account activation — click to filter pending invitations" data-testid={`sub-account-invite-pending-${sa.id}`}
                                    onClick={(e) => { e.stopPropagation(); setSubAccountPendingOnly(true); setSubAccountsPage(1); }}>
                                    <Mail className="w-3 h-3" /> Pending invite
                                  </Badge>
                                )}
                                {sa.invite_status === 'expired' && (
                                  <Badge variant="outline" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 gap-1 cursor-pointer hover:bg-orange-200 dark:hover:bg-orange-900/50" title="Activation link expired — click to filter pending invitations" data-testid={`sub-account-invite-expired-${sa.id}`}
                                    onClick={(e) => { e.stopPropagation(); setSubAccountPendingOnly(true); setSubAccountsPage(1); }}>
                                    <Mail className="w-3 h-3" /> Invite expired
                                  </Badge>
                                )}
                                {sa.invite_status === 'cancelled' && (
                                  <Badge variant="outline" className="bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-400 gap-1" title="Invitation cancelled - activation link revoked" data-testid={`sub-account-invite-cancelled-${sa.id}`}>
                                    <XCircle className="w-3 h-3" /> Invite cancelled
                                  </Badge>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {sa.last_login ? formatDT(sa.last_login) : 'Never'}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {/* Login as button */}
                            {sa.is_active !== false && !isSubAccountExpired(sa) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleLoginAsSubAccount(sa)}
                                title={`Login as ${sa.full_name}`}
                                className="hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-950 dark:hover:border-blue-700"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            )}
                            {/* Edit button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditSubAccount(sa)}
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {/* Delete button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSubAccount(sa.id)}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {/* Pagination footer always mounted for consistent card height. */}
              <PaginationFooter
                page={subAccountsPage}
                setPage={setSubAccountsPage}
                totalItems={subAccountsTotal}
                itemsPerPage={subAccountsPageSize}
                setItemsPerPage={setSubAccountsPageSize}
                testIdPrefix="sub-accounts"
                disabled={subAccountsLoading}
                alwaysShow
              />
            </CardContent>
          </Card>

          {/* Allocation Overview — owner-only: each Sub Account with its Devices & People side by side */}
          <AllocationOverview api={api} onEdit={handleEditSubAccount} refreshToken={subAccounts} />
        </div>
      )}

      {/* Add Sub Account Dialog */}
      <Dialog open={isAddSubAccountDialogOpen} onOpenChange={setIsAddSubAccountDialogOpen}>
        <DialogContent className="max-w-2xl h-[85vh] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Create Sub Account</DialogTitle>
            <DialogDescription>
              Create a new sub account with limited access to your fleet data.
            </DialogDescription>
          </DialogHeader>
          {/* Hidden decoy fields to prevent browser autofill */}
          <input type="text" name="decoy-username" style={{ display: 'none' }} autoComplete="username" tabIndex={-1} />
          <input type="password" name="decoy-password" style={{ display: 'none' }} autoComplete="current-password" tabIndex={-1} />
          
          {/* Tab Navigation */}
          <div className="flex gap-2 border-b">
            <Button
              variant="ghost"
              onClick={() => setActiveSubAccountTab('account')}
              className={activeSubAccountTab === 'account' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              Account
            </Button>
            <Button
              variant="ghost"
              onClick={() => setActiveSubAccountTab('privileges')}
              className={activeSubAccountTab === 'privileges' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              Privileges
            </Button>
            <Button
              variant="ghost"
              onClick={() => setActiveSubAccountTab('devices')}
              className={activeSubAccountTab === 'devices' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              Devices
            </Button>
            <Button
              variant="ghost"
              data-testid="subaccount-people-tab"
              onClick={() => setActiveSubAccountTab('people')}
              className={activeSubAccountTab === 'people' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              People
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto py-4">
            {/* Account Tab */}
            {activeSubAccountTab === 'account' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      placeholder="Enter full name"
                      value={subAccountForm.full_name}
                      onChange={(e) => setSubAccountForm({ ...subAccountForm, full_name: e.target.value })}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      placeholder="Enter username (optional)"
                      value={subAccountForm.username}
                      onChange={(e) => setSubAccountForm({ ...subAccountForm, username: e.target.value })}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                    <p className="text-xs text-muted-foreground">Leave empty to auto-generate from email</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email Address *</Label>
                    <Input
                      type="text"
                      inputMode="email"
                      placeholder="Enter email address"
                      value={subAccountForm.email}
                      onChange={(e) => setSubAccountForm({ ...subAccountForm, email: e.target.value })}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      name="subaccount-email-field"
                      data-testid="sub-account-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password{subAccountForm.send_invite ? '' : ' *'}</Label>
                    <Input
                      type="password"
                      placeholder={subAccountForm.send_invite ? 'Set by user via invitation' : 'Enter password (min 6 characters)'}
                      value={subAccountForm.password}
                      onChange={(e) => setSubAccountForm({ ...subAccountForm, password: e.target.value })}
                      disabled={subAccountForm.send_invite}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      name="subaccount-password-field"
                      data-testid="sub-account-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Number</Label>
                  <Input
                    type="tel"
                    placeholder="+61 4XX XXX XXX"
                    value={subAccountForm.mobile}
                    onChange={(e) => setSubAccountForm({ ...subAccountForm, mobile: e.target.value })}
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    data-testid="sub-account-mobile-input"
                  />
                  <p className="text-xs text-muted-foreground">Optional — required to send the invitation by SMS.</p>
                </div>

                {/* Send welcome invitation (mirrors the Admin "Add New User" modal) */}
                <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/60 dark:bg-blue-950/20 dark:border-blue-900 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="pr-3">
                      <p className="font-medium text-sm flex items-center gap-1.5">
                        <Mail className="w-4 h-4 text-[#2563eb]" /> Send welcome invitation
                      </p>
                      <p className="text-xs text-muted-foreground">
                        The user receives a secure link to accept the Terms &amp; Privacy Policy and set their own password.
                      </p>
                    </div>
                    <Switch
                      checked={!!subAccountForm.send_invite}
                      onCheckedChange={(v) => setSubAccountForm({ ...subAccountForm, send_invite: v })}
                      data-testid="sub-account-send-invite-toggle"
                    />
                  </div>
                  {subAccountForm.send_invite && (() => {
                    const emailInviteActive = !!((subAccountForm.email || '').trim()) && isNotificationChannelActive('notification_email');
                    const smsInviteActive = !!((subAccountForm.mobile || '').trim()) && isNotificationChannelActive('notification_sms');
                    return (
                      <div className="space-y-1.5 pt-2 border-t border-blue-100 dark:border-blue-900" data-testid="sub-account-invite-channels">
                        <span className="block text-xs text-muted-foreground">Deliver the invitation via (optional):</span>
                        <div className="flex items-center gap-5">
                          <label className={`flex items-center gap-2 text-sm ${emailInviteActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                            title={emailInviteActive ? '' : 'Enter an email above and enable your Email notification channel in Settings'}>
                            <Checkbox checked={!!subAccountForm.invite_email && emailInviteActive} disabled={!emailInviteActive}
                              onCheckedChange={(v) => setSubAccountForm({ ...subAccountForm, invite_email: !!v })} data-testid="sub-account-invite-email" />
                            Email
                          </label>
                          <label className={`flex items-center gap-2 text-sm ${smsInviteActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                            title={smsInviteActive ? '' : 'Enter a mobile number above and enable your SMS notification channel in Settings'}>
                            <Checkbox checked={!!subAccountForm.invite_sms && smsInviteActive} disabled={!smsInviteActive}
                              onCheckedChange={(v) => setSubAccountForm({ ...subAccountForm, invite_sms: !!v })} data-testid="sub-account-invite-sms" />
                            SMS
                          </label>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Tick a channel to deliver the link now, or leave both off and copy the link from the account&apos;s card afterwards.
                        </p>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Account Status Section */}
                {(() => {
                  // Check if account is expired based on form data (for consistency)
                  const isExpiredByDate = subAccountForm.expiry_enabled && subAccountForm.expiry_date && parseExpiryDate(subAccountForm.expiry_date) < new Date();
                  const effectiveStatus = isExpiredByDate ? false : subAccountForm.is_active;
                  
                  return (
                    <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${isExpiredByDate ? 'border-orange-300 dark:border-orange-700 bg-orange-50/50 dark:bg-orange-900/10' : 'border-slate-200 dark:border-slate-700'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          effectiveStatus 
                            ? 'bg-green-100 dark:bg-green-900/30' 
                            : 'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          {effectiveStatus ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : isExpiredByDate ? (
                            <Clock className="w-5 h-5 text-orange-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Account Status</p>
                          <p className="text-xs text-muted-foreground">
                            {isExpiredByDate 
                              ? 'Account expired - disable expiry to reactivate'
                              : effectiveStatus 
                                ? 'User can access the platform' 
                                : 'User is blocked from logging in'
                            }
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-medium ${
                          isExpiredByDate ? 'text-orange-600' : effectiveStatus ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isExpiredByDate ? 'Expired' : effectiveStatus ? 'Active' : 'Blocked'}
                        </span>
                        <Switch
                          checked={effectiveStatus}
                          onCheckedChange={(v) => handleSubAccountFormChange('is_active', v)}
                          disabled={isExpiredByDate}
                          title={isExpiredByDate ? 'Disable Account Expiry to manually control status' : undefined}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Account Expiry Section */}
                <div className="space-y-2">
                  <Label>Account Expiry (Optional)</Label>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={subAccountForm.expiry_enabled}
                      onCheckedChange={(v) => handleSubAccountFormChange('expiry_enabled', v)}
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          disabled={!subAccountForm.expiry_enabled}
                          className={`w-[280px] justify-start text-left font-normal ${!subAccountForm.expiry_enabled ? 'opacity-50' : ''} ${!subAccountForm.expiry_date ? 'text-muted-foreground' : ''}`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-slate-500 dark:text-slate-400" />
                          {subAccountForm.expiry_date 
                            ? format(parseExpiryDate(subAccountForm.expiry_date), "dd/MM/yyyy 'at' h:mm a")
                            : "Pick a date & time"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                        <Calendar
                          mode="single"
                          selected={subAccountForm.expiry_date ? parseExpiryDate(subAccountForm.expiry_date) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                              const existingDate = subAccountForm.expiry_date ? parseExpiryDate(subAccountForm.expiry_date) : null;
                              const hours = existingDate ? existingDate.getHours() : 23;
                              const minutes = existingDate ? existingDate.getMinutes() : 59;
                              localDate.setHours(hours, minutes, 59, 999);
                              handleSubAccountFormChange('expiry_date', localDate.toISOString());
                            }
                          }}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const checkDate = new Date(date);
                            checkDate.setHours(0, 0, 0, 0);
                            return checkDate < today;
                          }}
                          initialFocus
                        />
                        <div className="p-3 border-t border-border">
                          <Label className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                            <Clock className="w-3 h-3" />
                            Time (Local)
                          </Label>
                          <Input
                            type="time"
                            value={subAccountForm.expiry_date ? parseExpiryDate(subAccountForm.expiry_date)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '23:59'}
                            onChange={(e) => {
                              if (subAccountForm.expiry_date) {
                                const [hours, minutes] = e.target.value.split(':');
                                const date = parseExpiryDate(subAccountForm.expiry_date);
                                date.setHours(parseInt(hours), parseInt(minutes), 59, 999);
                                handleSubAccountFormChange('expiry_date', date.toISOString());
                              }
                            }}
                            className="w-full dark:[color-scheme:dark]"
                          />
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {subAccountForm.expiry_enabled 
                      ? `Account will be automatically blocked after this date/time (${Intl.DateTimeFormat().resolvedOptions().timeZone})` 
                      : 'Enable to set an automatic account expiry'}
                  </p>
                </div>
              </div>
            )}

            {/* Privileges Tab */}
            {activeSubAccountTab === 'privileges' && renderSubAccountPrivilegesTabBody()}
            {activeSubAccountTab === 'people' && renderSubAccountPeopleTabBody()}

            {/* Devices Tab */}
            {activeSubAccountTab === 'devices' && (
              <div className="h-full flex flex-col space-y-4">
                <div>
                  <Label className="text-base font-semibold">Device Access</Label>
                  <p className="text-xs text-muted-foreground">Select which devices this sub account can view (only your assigned devices are shown)</p>
                </div>
                
                {/* Search Filter */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search devices..."
                    value={deviceSearchFilter}
                    onChange={(e) => setDeviceSearchFilter(e.target.value)}
                    className="h-8 pl-10"
                  />
                </div>

                {renderSubAccountDeviceSelectionBar()}

                {filteredAvailableDevices.length > 0 ? (
                  <div className="border rounded-lg flex-1 min-h-0 flex flex-col">
                    <div className="flex-1 min-h-0 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox
                              checked={paginatedSubAccountDevices.length > 0 && paginatedSubAccountDevices.every(d => subAccountForm.selected_device_ids.includes(d.id))}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  const newIds = [...new Set([...subAccountForm.selected_device_ids, ...paginatedSubAccountDevices.map(d => d.id)])];
                                  setSubAccountForm({ ...subAccountForm, selected_device_ids: newIds });
                                } else {
                                  const filterIds = paginatedSubAccountDevices.map(d => d.id);
                                  setSubAccountForm({ 
                                    ...subAccountForm, 
                                    selected_device_ids: subAccountForm.selected_device_ids.filter(id => !filterIds.includes(id))
                                  });
                                }
                              }}
                            />
                          </TableHead>
                          <TableHead>Device</TableHead>
                          <TableHead>Registration</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedSubAccountDevices.map(device => (
                          <TableRow
                            key={device.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleSubAccountDevice(device.id)}
                            data-testid={`subaccount-device-row-${device.id}`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={subAccountForm.selected_device_ids.includes(device.id)}
                                tabIndex={-1}
                                className="pointer-events-none"
                              />
                            </TableCell>
                            <TableCell className="font-medium" data-testid={`subaccount-device-label-${device.id}`}>{deviceDisplayName(device)}</TableCell>
                            <TableCell>{device.registration || '-'}</TableCell>
                            <TableCell>
                              {getSubAccountDeviceStatusBadge(device.status)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                    <PaginationFooter
                      page={subDevicePage}
                      setPage={setSubDevicePage}
                      totalItems={filteredAvailableDevices.length}
                      itemsPerPage={subDevicePageSize}
                      setItemsPerPage={setSubDevicePageSize}
                      testIdPrefix="subaccount-devices"
                      scrollOnChange={false}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic p-3 bg-muted rounded-lg">
                    {deviceSearchFilter ? 'No devices match your search' : 'No devices assigned to your account'}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setIsAddSubAccountDialogOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreateSubAccount} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              Create Sub Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit Sub Account Dialog */}
      <Dialog open={isEditSubAccountDialogOpen} onOpenChange={(open) => {
        if (!open && subAccountFormHasChanges()) { setShowSubAccountUnsavedWarning(true); return; }
        setIsEditSubAccountDialogOpen(open);
      }}>
        <DialogContent className="max-w-2xl h-[85vh] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Sub Account</DialogTitle>
            <DialogDescription>
              Update sub account details and permissions.
            </DialogDescription>
          </DialogHeader>
          {/* Hidden decoy fields to prevent browser autofill */}
          <input type="text" name="decoy-edit-username" style={{ display: 'none' }} autoComplete="username" tabIndex={-1} />
          <input type="password" name="decoy-edit-password" style={{ display: 'none' }} autoComplete="current-password" tabIndex={-1} />
          
          {/* Tab Navigation */}
          <div className="flex gap-2 border-b">
            <Button
              variant="ghost"
              onClick={() => setActiveSubAccountTab('account')}
              className={activeSubAccountTab === 'account' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              Account
            </Button>
            <Button
              variant="ghost"
              onClick={() => setActiveSubAccountTab('privileges')}
              className={activeSubAccountTab === 'privileges' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              Privileges
            </Button>
            <Button
              variant="ghost"
              onClick={() => setActiveSubAccountTab('devices')}
              className={activeSubAccountTab === 'devices' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              Devices
            </Button>
            <Button
              variant="ghost"
              data-testid="subaccount-people-tab"
              onClick={() => setActiveSubAccountTab('people')}
              className={activeSubAccountTab === 'people' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              People
            </Button>
            <Button
              variant="ghost"
              data-testid="subaccount-audit-tab"
              onClick={() => { setActiveSubAccountTab('audit'); fetchSubAccountAudit(selectedSubAccount?.id); }}
              className={activeSubAccountTab === 'audit' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              Audit
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto py-4">
            {/* Account Tab */}
            {activeSubAccountTab === 'account' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      placeholder="Enter full name"
                      value={subAccountForm.full_name}
                      onChange={(e) => setSubAccountForm({ ...subAccountForm, full_name: e.target.value })}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      placeholder="Enter username"
                      value={subAccountForm.username}
                      onChange={(e) => setSubAccountForm({ ...subAccountForm, username: e.target.value })}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="text"
                      value={subAccountForm.email}
                      disabled
                      className="bg-muted"
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>New Password (leave blank to keep current)</Label>
                    <Input
                      type="password"
                      placeholder="Enter new password"
                      value={subAccountForm.password}
                      onChange={(e) => setSubAccountForm({ ...subAccountForm, password: e.target.value })}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      name="edit-subaccount-password-field"
                    />
                  </div>
                </div>
                
                {/* Account Status Section */}
                {(() => {
                  // Check if account is expired based on form data (for consistency)
                  const isExpiredByDate = subAccountForm.expiry_enabled && subAccountForm.expiry_date && parseExpiryDate(subAccountForm.expiry_date) < new Date();
                  const effectiveStatus = isExpiredByDate ? false : subAccountForm.is_active;
                  
                  return (
                    <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${isExpiredByDate ? 'border-orange-300 dark:border-orange-700 bg-orange-50/50 dark:bg-orange-900/10' : 'border-slate-200 dark:border-slate-700'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          effectiveStatus 
                            ? 'bg-green-100 dark:bg-green-900/30' 
                            : 'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          {effectiveStatus ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : isExpiredByDate ? (
                            <Clock className="w-5 h-5 text-orange-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Account Status</p>
                          <p className="text-xs text-muted-foreground">
                            {isExpiredByDate 
                              ? 'Account expired - disable expiry to reactivate'
                              : effectiveStatus 
                                ? 'User can access the platform' 
                                : 'User is blocked from logging in'
                            }
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-medium ${
                          isExpiredByDate ? 'text-orange-600' : effectiveStatus ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isExpiredByDate ? 'Expired' : effectiveStatus ? 'Active' : 'Blocked'}
                        </span>
                        <Switch
                          checked={effectiveStatus}
                          onCheckedChange={(v) => handleSubAccountFormChange('is_active', v)}
                          disabled={isExpiredByDate}
                          title={isExpiredByDate ? 'Disable Account Expiry to manually control status' : undefined}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Account Expiry Section */}
                <div className="space-y-2">
                  <Label>Account Expiry (Optional)</Label>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={subAccountForm.expiry_enabled}
                      onCheckedChange={(v) => handleSubAccountFormChange('expiry_enabled', v)}
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          disabled={!subAccountForm.expiry_enabled}
                          className={`w-[280px] justify-start text-left font-normal ${!subAccountForm.expiry_enabled ? 'opacity-50' : ''} ${!subAccountForm.expiry_date ? 'text-muted-foreground' : ''}`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-slate-500 dark:text-slate-400" />
                          {subAccountForm.expiry_date 
                            ? format(parseExpiryDate(subAccountForm.expiry_date), "dd/MM/yyyy 'at' h:mm a")
                            : "Pick a date & time"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                        <Calendar
                          mode="single"
                          selected={subAccountForm.expiry_date ? parseExpiryDate(subAccountForm.expiry_date) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                              const existingDate = subAccountForm.expiry_date ? parseExpiryDate(subAccountForm.expiry_date) : null;
                              const hours = existingDate ? existingDate.getHours() : 23;
                              const minutes = existingDate ? existingDate.getMinutes() : 59;
                              localDate.setHours(hours, minutes, 59, 999);
                              handleSubAccountFormChange('expiry_date', localDate.toISOString());
                            }
                          }}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const checkDate = new Date(date);
                            checkDate.setHours(0, 0, 0, 0);
                            return checkDate < today;
                          }}
                          initialFocus
                        />
                        <div className="p-3 border-t border-border">
                          <Label className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                            <Clock className="w-3 h-3" />
                            Time (Local)
                          </Label>
                          <Input
                            type="time"
                            value={subAccountForm.expiry_date ? parseExpiryDate(subAccountForm.expiry_date)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '23:59'}
                            onChange={(e) => {
                              if (subAccountForm.expiry_date) {
                                const [hours, minutes] = e.target.value.split(':');
                                const date = parseExpiryDate(subAccountForm.expiry_date);
                                date.setHours(parseInt(hours), parseInt(minutes), 59, 999);
                                handleSubAccountFormChange('expiry_date', date.toISOString());
                              }
                            }}
                            className="w-full dark:[color-scheme:dark]"
                          />
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {subAccountForm.expiry_enabled 
                      ? `Account will be automatically blocked after this date/time (${Intl.DateTimeFormat().resolvedOptions().timeZone})` 
                      : 'Enable to set an automatic account expiry'}
                  </p>
                </div>

                {/* Account Activation / Welcome Invitation (mirrors Admin Edit User) */}
                {(selectedSubAccount?.invite_status === 'pending_invitation' || selectedSubAccount?.invite_status === 'expired' || selectedSubAccount?.invite_status === 'cancelled' || selectedSubAccount?.invite_accepted_at) && (
                  <div className="space-y-3 pt-4 border-t" data-testid="sub-account-activation-section">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Account Activation
                    </Label>
                    {selectedSubAccount?.invite_accepted_at ? (
                      <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg p-3" data-testid="sub-account-invite-accepted-info">
                        <CheckCircle className="w-4 h-4" />
                        <span>Activated by the user{selectedSubAccount.invite_accepted_at ? ` on ${formatDT(selectedSubAccount.invite_accepted_at)}` : ''}.</span>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground">
                          {selectedSubAccount?.invite_status === 'cancelled'
                            ? 'This invitation was cancelled and the activation link no longer works. Correct the email/mobile above if it was sent to the wrong person, then resend a fresh invitation (valid 48 hours).'
                            : `This sub account has ${selectedSubAccount?.invite_status === 'expired' ? 'an expired' : 'a pending'} welcome invitation and has not set a password yet. Resend it by email and/or SMS, or copy a fresh activation link (valid 48 hours).`}
                        </p>
                        {selectedSubAccount?.invite_last_sent_at && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="sub-account-last-invite-summary">
                            <Clock className="w-3 h-3 shrink-0" />
                            Last invite: {(selectedSubAccount.invite_last_channels && selectedSubAccount.invite_last_channels.length)
                              ? `sent via ${selectedSubAccount.invite_last_channels.map(c => (c === 'sms' ? 'SMS' : 'Email')).join(' & ')}`
                              : 'link generated'} · {formatDT(selectedSubAccount.invite_last_sent_at)}
                          </p>
                        )}
                        {(() => {
                          const emailActive = !!((selectedSubAccount?.email || '').trim()) && isNotificationChannelActive('notification_email');
                          const smsActive = !!((selectedSubAccount?.mobile || '').trim()) && isNotificationChannelActive('notification_sms');
                          return (
                            <div className="flex items-center gap-5" data-testid="sub-account-resend-invite-channels">
                              <span className="text-xs text-muted-foreground">Send via:</span>
                              <label className={`flex items-center gap-2 text-sm ${emailActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                title={emailActive ? '' : 'Enable your Email notification channel in Settings'}>
                                <Checkbox checked={resendSubInviteEmail && emailActive} disabled={!emailActive}
                                  onCheckedChange={(v) => setResendSubInviteEmail(!!v)} data-testid="sub-account-resend-invite-email" />
                                Email
                              </label>
                              <label className={`flex items-center gap-2 text-sm ${smsActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                title={smsActive ? '' : 'Add a mobile number above and enable your SMS notification channel in Settings'}>
                                <Checkbox checked={resendSubInviteSms && smsActive} disabled={!smsActive}
                                  onCheckedChange={(v) => setResendSubInviteSms(!!v)} data-testid="sub-account-resend-invite-sms" />
                                SMS
                              </label>
                            </div>
                          );
                        })()}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            onClick={handleResendSubAccountInvite}
                            disabled={subInviteLoading}
                            data-testid="sub-account-resend-invite-btn"
                          >
                            {subInviteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                            {selectedSubAccount?.invite_status === 'cancelled' ? 'Resend new invitation' : 'Resend invitation'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleCopySubAccountInviteLink}
                            disabled={subInviteLoading}
                            data-testid="sub-account-copy-invite-link-btn"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy activation link
                          </Button>
                          {selectedSubAccount?.invite_status !== 'cancelled' && (
                            <Button
                              variant="outline"
                              onClick={handleCancelSubAccountInvite}
                              disabled={subInviteLoading}
                              className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950"
                              data-testid="sub-account-cancel-invite-btn"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Cancel Invite
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}


                {/* Quick Login Link Section */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Quick Login Link
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Generate a temporary URL that allows direct login without a password. Useful for support access or quick sharing.
                  </p>

                  {quickLoginUrl ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={quickLoginUrl}
                          readOnly
                          className="font-mono text-xs"
                          data-testid="sub-account-quick-login-url"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(quickLoginUrl);
                            toast.success('Quick login link copied to clipboard');
                          }}
                          className="shrink-0"
                          data-testid="sub-account-quick-login-copy-btn"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowSubQuickLoginQR(v => !v)}
                          className={`shrink-0 ${showSubQuickLoginQR ? 'border-blue-500 text-blue-600' : ''}`}
                          data-testid="sub-account-quick-login-qr-btn"
                        >
                          <QrCode className="w-4 h-4 mr-2" />
                          QR Code
                        </Button>
                      </div>
                      {showSubQuickLoginQR && (
                        <div className="flex flex-col items-center gap-3 p-4 rounded-lg border bg-muted/20" data-testid="sub-account-quick-login-qr-panel">
                          <div id="sub-account-quick-login-qr-canvas" className="p-3 bg-white rounded-lg border" data-testid="sub-account-quick-login-qr-code">
                            <QRCodeCanvas value={quickLoginUrl} size={200} level="M" />
                          </div>
                          <p className="text-xs text-muted-foreground text-center">Scan this code with a phone camera to log in instantly as this sub account.</p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const canvas = document.querySelector('#sub-account-quick-login-qr-canvas canvas');
                              if (!canvas) return;
                              const link = document.createElement('a');
                              link.download = 'sub-account-quick-login-qr.png';
                              link.href = canvas.toDataURL('image/png');
                              link.click();
                            }}
                            data-testid="sub-account-quick-login-qr-download-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download QR Code
                          </Button>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          try {
                            setQuickLoginLoading(true);
                            await api.delete(`/users/${selectedSubAccount.id}/quick-login-token`);
                            setQuickLoginUrl('');
                            setShowSubQuickLoginQR(false);
                            toast.success('Quick login link revoked');
                          } catch (error) {
                            toast.error('Failed to revoke quick login link');
                          } finally {
                            setQuickLoginLoading(false);
                          }
                        }}
                        disabled={quickLoginLoading}
                        data-testid="sub-account-quick-login-revoke-btn"
                      >
                        {quickLoginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Revoke Quick Login Link
                      </Button>
                      <p className="text-xs text-amber-600">
                        ⚠️ This link allows anyone with it to log in as this sub account. Revoke if no longer needed.
                      </p>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        try {
                          setQuickLoginLoading(true);
                          const response = await api.post(`/users/${selectedSubAccount.id}/quick-login-token`);
                          const token = response.data.quick_login_token;
                          setQuickLoginUrl(`${window.location.origin}/quick-login/${token}`);
                          toast.success('Quick login link generated');
                        } catch (error) {
                          toast.error('Failed to generate quick login link');
                        } finally {
                          setQuickLoginLoading(false);
                        }
                      }}
                      disabled={quickLoginLoading}
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      data-testid="sub-account-quick-login-generate-btn"
                    >
                      {quickLoginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                      Generate Quick Login Link
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Privileges Tab */}
            {activeSubAccountTab === 'privileges' && renderSubAccountPrivilegesTabBody()}
            {activeSubAccountTab === 'people' && renderSubAccountPeopleTabBody()}

            {/* Devices Tab */}
            {activeSubAccountTab === 'devices' && (
              <div className="h-full flex flex-col space-y-4">
                <div>
                  <Label className="text-base font-semibold">Device Access</Label>
                  <p className="text-xs text-muted-foreground">Select which devices this sub account can view (only your assigned devices are shown)</p>
                </div>
                
                {/* Search Filter */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search devices..."
                    value={deviceSearchFilter}
                    onChange={(e) => setDeviceSearchFilter(e.target.value)}
                    className="h-8 pl-10"
                  />
                </div>

                {renderSubAccountDeviceSelectionBar()}

                {filteredAvailableDevices.length > 0 ? (
                  <div className="border rounded-lg flex-1 min-h-0 flex flex-col">
                    <div className="flex-1 min-h-0 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox
                              checked={paginatedSubAccountDevices.length > 0 && paginatedSubAccountDevices.every(d => subAccountForm.selected_device_ids.includes(d.id))}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  const newIds = [...new Set([...subAccountForm.selected_device_ids, ...paginatedSubAccountDevices.map(d => d.id)])];
                                  setSubAccountForm({ ...subAccountForm, selected_device_ids: newIds });
                                } else {
                                  const filterIds = paginatedSubAccountDevices.map(d => d.id);
                                  setSubAccountForm({ 
                                    ...subAccountForm, 
                                    selected_device_ids: subAccountForm.selected_device_ids.filter(id => !filterIds.includes(id))
                                  });
                                }
                              }}
                            />
                          </TableHead>
                          <TableHead>Device</TableHead>
                          <TableHead>Registration</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedSubAccountDevices.map(device => (
                          <TableRow
                            key={device.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleSubAccountDevice(device.id)}
                            data-testid={`subaccount-device-row-${device.id}`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={subAccountForm.selected_device_ids.includes(device.id)}
                                tabIndex={-1}
                                className="pointer-events-none"
                              />
                            </TableCell>
                            <TableCell className="font-medium" data-testid={`subaccount-device-label-${device.id}`}>{deviceDisplayName(device)}</TableCell>
                            <TableCell>{device.registration || '-'}</TableCell>
                            <TableCell>
                              {getSubAccountDeviceStatusBadge(device.status)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                    <PaginationFooter
                      page={subDevicePage}
                      setPage={setSubDevicePage}
                      totalItems={filteredAvailableDevices.length}
                      itemsPerPage={subDevicePageSize}
                      setItemsPerPage={setSubDevicePageSize}
                      testIdPrefix="subaccount-devices"
                      scrollOnChange={false}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic p-3 bg-muted rounded-lg">
                    {deviceSearchFilter ? 'No devices match your search' : 'No devices assigned to your account'}
                  </p>
                )}
              </div>
            )}

            {/* Audit Tab — the sub account's audit trail (incl. Login As events) */}
            {activeSubAccountTab === 'audit' && (
              <div className="h-full flex flex-col space-y-4" data-testid="subaccount-audit-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Audit Trail</Label>
                    <p className="text-xs text-muted-foreground">Activity recorded for this sub account, including "Login As" (impersonation) events.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    data-testid="subaccount-audit-refresh"
                    onClick={() => fetchSubAccountAudit(selectedSubAccount?.id)}
                    disabled={subAccountAuditLoading}
                  >
                    {subAccountAuditLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </Button>
                </div>
                <AuditLogList
                  logs={subAccountAuditLogs}
                  loading={subAccountAuditLoading}
                  formatTimestamp={formatSubAccountTimestamp}
                  heightClass="flex-1 min-h-0"
                  emptyText="No audit activity yet"
                  emptySubText="Login As and other actions will appear here"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => {
              if (subAccountFormHasChanges()) { setShowSubAccountUnsavedWarning(true); return; }
              setIsEditSubAccountDialogOpen(false);
            }}>Cancel</Button>
            <Button size="sm" onClick={handleUpdateSubAccount} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={!!pendingSettingsAction} onOpenChange={(open) => { if (!open) { setPendingSettingsAction(null); setSettingsDeleteConfirmText(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" />{pendingSettingsAction?.title}</DialogTitle>
            <DialogDescription>{pendingSettingsAction?.message}</DialogDescription>
          </DialogHeader>
          {pendingSettingsAction?.requireTyped && (
            <div className="space-y-3">
              <p className="text-sm">To confirm, type the following exactly:</p>
              <code className="block p-2 bg-muted rounded text-sm font-mono font-bold">DELETE</code>
              <Input
                value={settingsDeleteConfirmText}
                onChange={(e) => setSettingsDeleteConfirmText(e.target.value)}
                placeholder="Type confirmation text..."
                className="font-mono"
                autoFocus
                data-testid="settings-delete-confirm-input"
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => { setPendingSettingsAction(null); setSettingsDeleteConfirmText(''); }}>Cancel</Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={pendingSettingsAction?.requireTyped && settingsDeleteConfirmText !== 'DELETE'}
              data-testid="settings-confirm-action-button"
              onClick={async () => { await pendingSettingsAction?.action(); setPendingSettingsAction(null); setSettingsDeleteConfirmText(''); }}
            >Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub Account Unsaved Changes Warning */}
      <Dialog open={showSubAccountUnsavedWarning} onOpenChange={setShowSubAccountUnsavedWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Unsaved Changes</DialogTitle>
            <DialogDescription>You have unsaved changes. Are you sure you want to leave? Your changes will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowSubAccountUnsavedWarning(false)}>Stay</Button>
            <Button size="sm" variant="destructive" onClick={() => { setShowSubAccountUnsavedWarning(false); setIsEditSubAccountDialogOpen(false); }}>Discard Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};
